import React, { useEffect, useRef, useState, useCallback, useImperativeHandle } from 'react';
import { Feature, Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import 'ol/ol.css';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import {fromLonLat, transform} from 'ol/proj';
import { boundingExtent, extend as extendExtent } from 'ol/extent';
import {Icon, Style, Fill, Stroke, Circle as CircleStyle, RegularShape} from 'ol/style.js';
import Control from 'ol/control/Control';
import GeoJSON from 'ol/format/GeoJSON';
import mapMarker from "../../assets/icons/svg/map-marker.svg";
import fireTruckIcon from "../../assets/icons/svg/fire-truck.svg";
import gasStationIcon from "../../assets/icons/svg/gas-station.svg";
import monumentIcon from "../../assets/icons/svg/monument-historique.svg";
import arretePerilsIcon from "../../assets/icons/svg/arrete-peril.svg";
import { useTranslation } from "react-i18next";
import { getElevation } from "../address/data4homeAPI";
import moment from 'moment';
import SidePanel, { PanelContentType } from '../../components/SidePanel/SidePanel';
import { RISK_WMS_LAYERS } from "./core/riskLayerCatalog";
import { createGeoServerRiskTileLayer } from "./core/geoServerRiskLayer";

// TODO: add a selection layer always on top, assign the selected feature to this layer, and remove it when the feature is deselected
// Color theme for map layers
const MAP_COLORS = {
  // Base layers
  trees: {
    fill: '#2e7d32',
    stroke: '#ffffff',
    strokeWidth: 1,
    radius: 5
  },
  railRoads: {
    stroke: '#8B4513', // Dark brown
    width: 3
  },
  fireStations: {
    fill: 'rgba(198, 40, 40, 0.35)',
    stroke: '#c62828',
    width: 2
  },
  gasStations: {
    stroke: '#ef6c00',
  },
  waterWays: {
    stroke: '#3364ff', // Blue
    width: 3,
    closestPoint: {
      fill: '#0000FF',
      stroke: '#ffffff',
      strokeWidth: 2,
      radius: 6
    }
  },
  buildings: {
    fill: 'rgba(0, 0, 119, 0.3)',
    stroke: '#0000FF',
    width: 2
  },
  parcels: {
    fill: 'rgba(4, 236, 4, 0.3)',
    stroke: '#00FF00',
    width: 2
  },
  // Place-copro unifié : bâtiment au lieu + parcelles au lieu + bâtiments/parcelles copro
  placeCoproBuildingAtLocation: {
    fill: 'rgba(0, 0, 180, 0.35)',
    stroke: '#0000b4',
    width: 2
  },
  placeCoproParcelsAtLocation: {
    fill: 'rgba(4, 236, 4, 0.3)',
    stroke: '#00AA00',
    width: 2
  },
  placeCoproParcelsAtLocationHighlighted: {
    fill: 'rgba(0, 255, 120, 0.65)',
    stroke: '#00DD00',
    width: 5
  },
  placeCoproCoproBuildings: {
    fill: 'rgba(0, 200, 255, 0.3)',
    stroke: '#0099cc',
    width: 2
  },
  placeCoproCoproParcels: {
    fill: 'rgba(4, 236, 4, 0.2)',
    stroke: '#008800',
    width: 1.5
  },
  /** Parcelles cadastrales de la copro sélectionnée (jaune) */
  placeCoproParcellesCadastreCopro: {
    fill: 'rgba(255, 235, 0, 0.5)',
    stroke: '#d4a800',
    width: 2
  },
  /** Point position GPS de la copro (jaune comme la parcelle, contour orange pour montrer le lien) */
  placeCoproPositionMarker: {
    fill: 'rgba(255, 235, 0, 0.5)',
    stroke: '#e65100',
    width: 2
  },
  placeCoproGroupesAtLocation: {
    fill: 'rgba(100, 100, 255, 0.25)',
    stroke: '#4444cc',
    width: 1.5
  },
  placeCoproGroupesCopro: {
    fill: 'rgba(0, 200, 255, 0.25)',
    stroke: '#0099cc',
    width: 1.5
  },
  placeCoproAdressesAtLocation: {
    fill: '#c62828',
    stroke: '#fff',
    width: 1
  },
  placeCoproAdressesCopro: {
    fill: '#1565c0',
    stroke: '#fff',
    width: 1
  },

  // heavy rain observations
  heavyRain: {
    fill: 'rgba(255, 0, 0, 0.5)',
    stroke: '#ffffff',
    strokeWidth: 2,
    radius: 20,
    selected: {
      fill: 'rgba(255, 165, 0, 1)',
      stroke: '#ffffff',
      strokeWidth: 3
    }
  },

  // arrete perils
  arretePerils: {
    fill: 'rgba(255, 0, 0, 0.8)',
    stroke: '#ffffff',
    strokeWidth: 2,
    radius: 5,
    selected: {
      fill: 'rgba(255, 165, 0, 1)',
      stroke: '#ffffff',
      strokeWidth: 3
    }
  },
  
  // Historic monuments
  historicMonuments: {
    fill: 'rgba(0, 0, 0, 0.8)',
    color: 'rgba(0, 0, 0, 0.8)',
    size: 24,
    selected: {
      color: 'rgba(255, 166, 0, 0.77)', // Orange for selected state
      size: 28
    }
  },
  
  // Zones surveillées
  zonesSurveillees: {
    fill: 'rgba(255, 165, 0, 0.3)', // Light orange
    stroke: 'rgba(0, 0, 0, 0.7)', // Black stroke
    strokeWidth: 3,
    pattern: {
      color: 'rgba(0, 0, 0, 0.1)', // Black with low opacity for normal state
      lineWidth: 4,
      spacing: 16
    },
    selected: {
      fill: 'rgb(255, 166, 0)',
      stroke: 'rgba(255, 165, 0, 1)',
      strokeWidth: 3,
      pattern: {
        color: 'rgba(255, 165, 0, 0.3)', // Darker for selected state
        lineWidth: 4,
        spacing: 16
      }
    }
  },
  
  // Zone types specific colors
  zoneTypes: {
    qpv: '#FF6B6B', // Red
    qpvAncienDegrade: '#FF8E53', // Orange
    qva: '#4ECDC4', // Turquoise
    zus: '#45B7D1', // Blue
    zfu: '#96CEB4', // Green
    npnru: '#A66DD4' // Purple
  },
  
  // UI colors
  ui: {
    measure: {
      fill: '#000000',
      stroke: '#ffffff',
      strokeWidth: 2
    },
    measureLine: {
      color: '#000000',
      width: 2
    },
    overlay: {
      dark: 'rgba(0,0,0,0.4)',
      darker: 'rgba(0,0,0,0.6)',
      error: 'rgb(244, 82, 77)'
    }
  },

  /** Points de la page courante du portefeuille (losange sélectionné, distinct du cercle orange). */
  placeCoproPortfolioPoint: {
    fill: '#5c6bc0',
    stroke: '#ffffff',
    width: 2,
    radius: 6,
  },
  placeCoproPortfolioSelected: {
    fill: '#283593',
    stroke: '#ffffff',
    width: 2,
    radius: 12,
  },
} as const;

const PORTFOLIO_POINT_STYLE = new Style({
  zIndex: 1,
  image: new CircleStyle({
    radius: MAP_COLORS.placeCoproPortfolioPoint.radius,
    fill: new Fill({ color: MAP_COLORS.placeCoproPortfolioPoint.fill }),
    stroke: new Stroke({
      color: MAP_COLORS.placeCoproPortfolioPoint.stroke,
      width: MAP_COLORS.placeCoproPortfolioPoint.width,
    }),
  }),
});

const PORTFOLIO_SELECTED_STYLE = new Style({
  zIndex: 2,
  image: new RegularShape({
    points: 4,
    radius: MAP_COLORS.placeCoproPortfolioSelected.radius,
    angle: Math.PI / 4,
    fill: new Fill({ color: MAP_COLORS.placeCoproPortfolioSelected.fill }),
    stroke: new Stroke({
      color: MAP_COLORS.placeCoproPortfolioSelected.stroke,
      width: MAP_COLORS.placeCoproPortfolioSelected.width,
    }),
  }),
});

type layerMapItem = {
    name: string,
    minZoom?: number,
    maxZoom?: number,
    injectTileBboxViewParams?: boolean,
}

// Catalogue unique : voir `core/riskLayerCatalog.ts`
const layerMap: Record<string, layerMapItem | null> = RISK_WMS_LAYERS as Record<
  string,
  layerMapItem | null
>;

const rcpStyleMap: Record<string, string | null> = {
    rcp26: "multi_rcp_selection_26",
    rcp45: "multi_rcp_selection_45",
    rcp85: "multi_rcp_selection_85",
}

// Function to calculate distance between two points in meters
function calculateDistance(point1: number[], point2: number[]): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = point1[1] * Math.PI / 180;
    const φ2 = point2[1] * Math.PI / 180;
    const Δφ = (point2[1] - point1[1]) * Math.PI / 180;
    const Δλ = (point2[0] - point1[0]) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
}

// Function to create a diagonal hatching pattern
function createHatchingPattern(strokeColor: string = 'rgba(0, 0, 0, 0.1)', lineWidth: number = 3, spacing: number = 16): CanvasPattern | null {
    const patternCanvas = document.createElement('canvas');
    const ctx = patternCanvas.getContext('2d');
    
    if (!ctx) return null;
    
    // Set pattern size
    patternCanvas.width = 80;
    patternCanvas.height = 80;
    
    // Clear canvas with transparent background
    ctx.clearRect(0, 0, 80, 80);
    
    // Draw diagonal lines at 45 degrees with configurable parameters
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    
    // Draw diagonal lines from top-left to bottom-right with configurable spacing
    for (let i = -80; i <= 80; i += spacing) {
        ctx.moveTo(i, 0);
        ctx.lineTo(i + 80, 80);
    }
    
    ctx.stroke();
    
    return ctx.createPattern(patternCanvas, 'repeat');
}

// Function to check if a feature will be hidden under the panel
function isFeatureUnderPanel(coordinate: number[], map: Map): boolean {
    const pixel = map.getPixelFromCoordinate(coordinate);
    const mapSize = map.getSize();
    const panelWidth = 350;
    const margin = 50; // Safety margin
    
    // Check if the feature is in the area that will be hidden
    return mapSize ? pixel[0] > (mapSize[0] - panelWidth - margin) : false;
}

// Function to pan the map to avoid the panel covering the feature
function panToAvoidPanel(map: Map, coordinate: number[]): void {
    const currentCenter = map.getView().getCenter();
    const metersPerPixel = map.getView().getResolution();
    
    // Check for null values
    if (!currentCenter || !metersPerPixel) return;
    
    // Distance to pan in pixels
    const panDistancePixels = 400; // 350px panel + 50px margin
    
    // Convert to meters
    const panDistanceMeters = panDistancePixels * metersPerPixel;
    
    // Calculate new center (pan towards east to move the feature left)
    const newCenter = [
        currentCenter[0] + panDistanceMeters,
        currentCenter[1]
    ];
    
    // Animate the pan
    map.getView().animate({
        center: newCenter,
        duration: 500
    });
}

// TODO: find a way to integrate with react
// Custom control for measure button (top-right, below info)
class MeasureControl extends Control {
  private button: HTMLButtonElement;
  private onClickHandler: (() => void) | null = null;

  constructor(onClick: () => void) {
    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      bottom: 10px;
      left: 10px;
      z-index: 1000;
    `;
    
    const button = document.createElement('button');
    button.innerHTML = '📏';
    button.style.cssText = `
      background-color: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      padding: 8px;
      margin: 4px;
      cursor: pointer;
      font-size: 16px;
      backdrop-filter: blur(10px);
      transition: all 0.2s ease;
    `;
    
    const handleClick = () => {
      if (this.onClickHandler) {
        this.onClickHandler();
      }
    };
    
    button.addEventListener('click', handleClick);
    button.addEventListener('mouseenter', () => {
      if (!button.classList.contains('active')) {
        button.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
        button.style.transform = 'scale(1.05)';
      }
    });
    button.addEventListener('mouseleave', () => {
      if (!button.classList.contains('active')) {
        button.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        button.style.transform = 'scale(1)';
      }
    });

    container.appendChild(button);

    super({
      element: container,
      target: undefined
    });

    this.button = button;
    this.onClickHandler = onClick;
  }

  setActive(active: boolean) {
    if (active) {
      this.button.classList.add('active');
      this.button.style.backgroundColor = '#2196f3';
      this.button.style.color = 'white';
      this.button.style.transform = 'scale(1.05)';
    } else {
      this.button.classList.remove('active');
      this.button.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
      this.button.style.color = 'black';
      this.button.style.transform = 'scale(1)';
    }
  }

  setOnClick(handler: () => void) {
    this.onClickHandler = handler;
  }
}

// Custom control for GPS coordinate pick mode (select address by clicking on map)
class GpsPickControl extends Control {
  private button: HTMLButtonElement;
  private onClickHandler: (() => void) | null = null;

  constructor(onClick: () => void) {
    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      bottom: 10px;
      left: 50px;
      z-index: 1000;
    `;

    const button = document.createElement('button');
    button.innerHTML = '📍';
    button.title = 'Sélectionner une adresse sur la carte';
    button.style.cssText = `
      background-color: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      padding: 8px;
      margin: 4px;
      cursor: pointer;
      font-size: 16px;
      backdrop-filter: blur(10px);
      transition: all 0.2s ease;
    `;

    button.addEventListener('click', () => {
      if (this.onClickHandler) this.onClickHandler();
    });
    button.addEventListener('mouseenter', () => {
      if (!button.classList.contains('active')) {
        button.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
        button.style.transform = 'scale(1.05)';
      }
    });
    button.addEventListener('mouseleave', () => {
      if (!button.classList.contains('active')) {
        button.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        button.style.transform = 'scale(1)';
      }
    });

    container.appendChild(button);
    super({ element: container, target: undefined });
    this.button = button;
    this.onClickHandler = onClick;
  }

  setActive(active: boolean) {
    if (active) {
      this.button.classList.add('active');
      this.button.style.backgroundColor = '#2196f3';
      this.button.style.color = 'white';
      this.button.style.transform = 'scale(1.05)';
    } else {
      this.button.classList.remove('active');
      this.button.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
      this.button.style.color = 'black';
      this.button.style.transform = 'scale(1)';
    }
  }

  setOnClick(handler: () => void) {
    this.onClickHandler = handler;
  }
}

// TODO: find a way to integrate with react
// Custom control for measure info panel
class MeasureInfoControl extends Control {
  private panel: HTMLDivElement;

  constructor() {
    const panel = document.createElement('div');
    panel.innerHTML = `
      <div id="measure-info-panel" style="
        position: absolute;
        bottom: 16px;
        left: 60px;
        background-color: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(10px);
        border-radius: 12px;
        padding: 16px;
        max-width: 300px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        border: 1px solid rgba(255, 255, 255, 0.2);
        display: none;
        pointer-events: auto;
      ">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <h6 style="margin: 0; font-size: 14px; font-weight: 600;">MESURE</h6>
          <button id="measure-close" style="
            background: none;
            border: none;
            font-size: 18px;
            cursor: pointer;
            padding: 0;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
          ">x</button>
        </div>
        <p id="measure-distance" style="margin: 0; font-size: 14px; color: #666;">Distance: -</p>
        <p id="measure-altitude" style="margin: 0; font-size: 14px; color: #666;">Altitude: -</p>
      </div>
    `;
    panel.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
    `;

    super({
      element: panel,
      target: undefined
    });

    this.panel = panel;
  }

  getPanel(): HTMLDivElement {
    return this.panel;
  }

  getInfoPanel(): HTMLDivElement | null {
    return this.panel.querySelector('#measure-info-panel') as HTMLDivElement;
  }
}


// OLMap Ref Interface
export interface OLMapRef {
  highlightHistoricMonuments: () => void;
  clearHistoricMonumentHighlight: () => void;
  highlightZonesSurveillees: () => void;
  clearZoneSurveilleeHighlight: () => void;
  centerOnFireStation: () => void;
  /** Revient à la vue d'avant le premier recentrage sur une ligne du portefeuille. */
  restorePortfolioHomeView: () => void;
  centerOnGasStation: () => void;
}

/**
 * OLMap Component - OpenLayers Map with multiple data layers
 * 
 * =============================================================================
 * LAYER HIERARCHY & Z-INDEX MANAGEMENT
 * =============================================================================
 * 
 * The map uses a carefully designed layer hierarchy to ensure proper visual ordering:
 * 
 * BASE LAYER (zIndex: 0)
 * - OSM Tile Layer: OpenStreetMap background tiles
 * 
 * DATA LAYERS (zIndex: 9-16)
 * 9  - Data Layer (Risk layers from GeoServer)
 *      - Dynamic WMS layers based on selectedRisk prop
 *      - Fire, drought, flood, wind, heavy rain, hail storm maps
 *      - Opacity: 0.6 for transparency over base layer
 * 
 * 10 - Zones Surveillées Layer
 *      - Pattern: Diagonal hatching with configurable colors
 *      - Normal state: Light pattern with black stroke
 *      - Selected state: Darker pattern with orange stroke
 *      - Feature ordering: Selected zones (zIndex: 1) above normal zones (zIndex: 0)
 * 
 * 11 - Rail Roads Layer
 *      - Brown railway lines with closest point markers
 *      - Style: Dark brown stroke (#8B4513, width: 3)
 * 
 * 12 - Water Ways Layer
 *      - Blue water courses (rivers, streams)
 *      - Style: Blue stroke (#3364ff, width: 3)
 *      - Closest point marker (blue circle)
 * 
 * 13 - Building & Parcel Layers
 *      - Building footprints: Blue fill with blue stroke
 *      - Parcel boundaries: Green fill with green stroke
 *      - Both use semi-transparent fills for overlay effect
 * 
 * 14 - Heavy Rain Layer
 *      - Red circles for heavy rain observations
 *      - Interactive: Click to show details in side panel
 *      - Hover effects: Cursor changes to pointer
 * 
 * 30 - Location Marker
 *      - Main address marker (custom SVG pin icon)
 *      - Above every other layer, including fire stations and the measure tool
 * 
 * 16 - Historic Monuments Layer
 *      - Purple circles for historic monuments
 *      - Interactive: Click to show details in side panel
 *      - Hover effects: Cursor changes to pointer
 * 
 * 17 - Arrete Perils Layer
 *      - Orange circles for arrete perils
 *      - Interactive: Click to show details in side panel
 *      - Hover effects: Cursor changes to pointer
 *
 * 18 - Fire Stations Layer
 *      - Station polygon and fire truck icon
 *      - Above historic monuments and ESWD heavy-rain events
 * 
 * DYNAMIC LAYERS (zIndex: 20+)
 * - Measure Layer: Temporary measurement lines and markers
 * - Closest Point Layer: Special markers for closest features
 * 
 * =============================================================================
 * STYLE MANAGEMENT SYSTEM
 * =============================================================================
 * 
 * The component uses a sophisticated style management system:
 * 
 * 1. THEME-BASED COLORS (MAP_COLORS constant)
 *    - Centralized color definitions for consistency
 *    - Support for normal and selected states
 *    - Pattern configuration (color, lineWidth, spacing)
 * 
 * 2. STYLE FUNCTIONS
 *    - Dynamic styling based on feature properties
 *    - Conditional appearance (normal vs selected)
 *    - Z-index management within layers
 * 
 * 3. PATTERN SYSTEM
 *    - Configurable diagonal hatching patterns
 *    - Theme-integrated colors and parameters
 *    - Support for different states (normal/selected)
 * 
 * =============================================================================
 * INTERACTION MANAGEMENT
 * =============================================================================
 * 
 * The map handles multiple types of user interactions:
 * 
 * 1. CLICK INTERACTIONS
 *    - Heavy Rain: Show observation details in side panel
 *    - Historic Monuments: Show monument details in side panel
 *    - Zones Surveillées: Show zone details in side panel
 *    - Panel positioning: Auto-pan to avoid covering clicked features
 * 
 * 2. HOVER INTERACTIONS
 *    - Cursor changes to pointer over interactive features
 *    - Disabled during measure mode
 *    - Layer-specific hover detection
 * 
 * 3. SELECTION MANAGEMENT
 *    - Feature state tracking with 'isSelected' property
 *    - Visual feedback with different styles
 *    - Z-index ordering for selected features
 * 
 * 4. MEASURE MODE
 *    - Toggle-able measurement tool
 *    - Click to measure distance from center point
 *    - Visual feedback with lines and markers
 *    - Altitude information via elevation API
 * 
 * =============================================================================
 * SIDE PANEL INTEGRATION
 * =============================================================================
 * 
 * The map integrates with a side panel system:
 * - Panel opens on feature click
 * - Auto-pan to avoid covering selected features
 * - Different content types: heavyRain, historicMonuments, zonesSurveillees
 * - Panel state management with React hooks
 */
const OLMap = React.forwardRef<OLMapRef, {
  style: any, 
  selectedRisk: any, 
  selectedRcp: any, 
  center?: any, 
  zoom?: any,
  onMenuClick?: () => void,
  onLayersClick?: () => void,
  onInfoClick?: () => void,
  address?: string,
  lat?: number,
  lng?: number,
  data4HomeInfo?: any,
  trees?: any,
  waterWays?: any,
  railRoads?: any,
  fireStations?: any,
  gasStations?: any,
  heavyRain?: any,
  historicMonuments?: any,
  zonesSurveillees?: any,
  arretePerils?: any,
  /** Données place-copro simplifiées : liste de fiches + nearbyCopro + buildingGroup (Feature GeoJSON). La parcelle au point GPS est gérée par /place. */
  placeCoproData?: {
    copros?: Array<{ lat?: number; long?: number; parcelles_cadastre?: Array<{ id: string; geometry?: any }> }>;
    nearbyCopro?: { type: 'Feature'; geometry?: any; properties?: { lat?: number; long?: number; parcelles_cadastre?: Array<{ id: string; geometry?: any }> } };
    /** Feature GeoJSON (geometry + properties: surface, hauteur_max, nombre_de_niveaux) pour afficher le groupe de bâtiment sur la carte à la place du bâtiment/parcelle /place */
    buildingGroup?: { type: 'Feature'; geometry?: GeoJSON.Geometry | null; properties?: { surface?: number | null; hauteur_max?: number | null; nombre_de_niveaux?: number | null } } | null;
  } | null,
  /** Index de la fiche copro sélectionnée (pour parcelles + position GPS sur la carte) */
  placeCoproSelectedIndex?: number,
  /** 'place' = données /place, 'placeCopro' = parcelle + parcelles copro + position copro */
  mapDataMode?: 'place' | 'placeCopro',
  /** Points de la page courante du portefeuille. Null ou vide : rien à tracer (tiroir fermé). */
  portfolioPoints?: Array<{ id: string; lat: number; long: number }> | null,
  /** Ligne cliquée : recentre et pose un marqueur distinct du point orange de la copro recherchée. */
  portfolioSelectedId?: string | null,
  /** Callback when user selects a point on map (GPS pick mode). Receives lat, lng in WGS84. */
  onCoordinatesSelected?: (lat: number, lng: number) => void
}>(({
  style, 
  selectedRisk, 
  selectedRcp, 
  center, 
  zoom, 
  onMenuClick, 
  onLayersClick, 
  onInfoClick,
  address,
  lat,
  lng,
  data4HomeInfo,
  trees,
  waterWays,
  railRoads,
  fireStations,
  gasStations,
  heavyRain,
  historicMonuments,
  zonesSurveillees,
  arretePerils,
  placeCoproData = null,
  placeCoproSelectedIndex = 0,
  mapDataMode = 'place',
  portfolioPoints = null,
  portfolioSelectedId = null,
  onCoordinatesSelected
}, ref) => {
    const mapElement = useRef<HTMLDivElement | null>(null);
    const map = useRef<Map | null>(null);
    const dataLayer = useRef<any | null>(null);
    const markerLayer = useRef<any | null>(null);
    const osmLayer = useRef<any | null>(null);
    const buildingLayer = useRef<any | null>(null);
    const parcelLayer = useRef<any | null>(null);
    const treesLayer = useRef<any | null>(null);
    const waterWaysLayer = useRef<any | null>(null);
    const railRoadsLayer = useRef<any | null>(null);
    const fireStationsLayer = useRef<any | null>(null);
    const fireStationRouteLayer = useRef<any | null>(null);
    const centerOnFireStationRef = useRef<() => void>(() => {});
    const gasStationsLayer = useRef<any | null>(null);
    const centerOnGasStationRef = useRef<() => void>(() => {});
    const heavyRainLayer = useRef<any | null>(null);
    const selectedHeavyRainFeature = useRef<any | null>(null);
    const historicMonumentsLayer = useRef<any | null>(null);
    const selectedHistoricMonuments = useRef<any[]>([]);
    const selectedHistoricMonumentFeatures = useRef<any[]>([]);
    const zonesSurveilleesLayer = useRef<any | null>(null);
    const selectedZonesSurveillees = useRef<any[]>([]);
    const selectedZoneSurveilleeFeatures = useRef<any[]>([]);
    const arretePerilsLayer = useRef<any | null>(null);
    const selectedArretePerils = useRef<any[]>([]);
    const selectedArretePerilFeatures = useRef<any[]>([]);
    const placeCoproParcellesCoproLayer = useRef<any | null>(null);
    const placeCoproMarkerLayer = useRef<any | null>(null);
    const placeCoproBuildingGroupLayer = useRef<any | null>(null);
    const placeCoproPortfolioLayer = useRef<any | null>(null);
    const lastPortfolioFocusId = useRef<string | null>(null);
    const portfolioHomeView = useRef<{ center: number[]; zoom: number | undefined } | null>(null);
    const closestPointLayer = useRef<any | null>(null);
    const measureLayer = useRef<any | null>(null);
    const measureInfoControl = useRef<MeasureInfoControl | null>(null);
    const measureControl = useRef<MeasureControl | null>(null);
    const gpsPickControl = useRef<GpsPickControl | null>(null);
    const [isMeasureMode, setIsMeasureMode] = useState(false);
    const [isGpsPickMode, setIsGpsPickMode] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);
    const [panelContentType, setPanelContentType] = useState<PanelContentType>(null);
    const [heavyRainFeature, setHeavyRainFeature] = useState<{date: string, [key: string]: unknown} | null>(null);
    const [historicMonumentFeatures, setHistoricMonumentFeatures] = useState<any[]>([]);
    const [zoneSurveilleeFeatures, setZoneSurveilleeFeatures] = useState<any[]>([]);
    const [arretePerilsFeatures, setArretePerilsFeatures] = useState<any[]>([]);
    const { t } = useTranslation();

    // Apply highlight styles to specific historic monuments (called when monuments are selected from panel)
    const highlightSelectedHistoricMonuments = useCallback((monuments: any[]) => {
        if (!historicMonumentsLayer.current || !map.current) return;
        
        // Clear previous highlights for historic monuments
        selectedHistoricMonumentFeatures.current.forEach(feature => {
            feature.setStyle(undefined);
        });
        selectedHistoricMonumentFeatures.current = [];
        
        // Clear previous highlights for zones surveillées
        selectedZoneSurveilleeFeatures.current.forEach(feature => {
            feature.set('isSelected', false);
        });
        selectedZoneSurveilleeFeatures.current = [];
        
        // Find and highlight the selected monuments
        const source = historicMonumentsLayer.current.getSource();
        if (source) {
            source.forEachFeature((feature: any) => {
                const properties = feature.getProperties();
                const monumentId = properties.id || properties.merimee_id || properties.reference;
                
                // Check if this monument is in the selected list
                const isSelected = monuments.some(monument => {
                    const selectedId = monument.properties?.id || monument.properties?.merimee_id || monument.properties?.reference;
                    return selectedId === monumentId;
                });
                
                if (isSelected) {
                    // Apply highlight style with colored icon
                    feature.setStyle(new Style({
                        image: new Icon({
                            src: monumentIcon,
                            anchor: [0.5, 0.5], // Anchor at center
                            anchorXUnits: 'fraction',
                            anchorYUnits: 'fraction',
                            scale: 1.1, // Slightly larger for selected state
                            color: MAP_COLORS.historicMonuments.selected.color // Orange color
                        })
                    }));
                    selectedHistoricMonumentFeatures.current.push(feature);
                }
            });
        }
    }, [historicMonumentsLayer, map]);

    // Show all historic monuments on map and open side panel (called when clicking monument vignette)
    const highlightHistoricMonuments = useCallback(() => {
        if (!historicMonuments?.features?.length || !map.current) return;
        
        // Clear previous highlights for heavy rain
        if (selectedHeavyRainFeature.current) {
            selectedHeavyRainFeature.current.setStyle(undefined);
            selectedHeavyRainFeature.current = null;
        }
        
        // Clear previous highlights for zones surveillées
        selectedZoneSurveilleeFeatures.current.forEach(feature => {
            feature.set('isSelected', false);
        });
        selectedZoneSurveilleeFeatures.current = [];
        
        // 1. Calculate bounds for all monuments
        const coordinates = historicMonuments.features.map((f: any) => f.geometry.coordinates);
        const extent = coordinates.reduce((acc: any, coord: any) => {
            const [lng, lat] = coord;
            if (!acc.minLng || lng < acc.minLng) acc.minLng = lng;
            if (!acc.maxLng || lng > acc.maxLng) acc.maxLng = lng;
            if (!acc.minLat || lat < acc.minLat) acc.minLat = lat;
            if (!acc.maxLat || lat > acc.maxLat) acc.maxLat = lat;
            return acc;
        }, {});
        
        // 2. Pan and zoom to show all monuments
        if (extent.minLng !== undefined) {
            const bounds = [extent.minLng, extent.minLat, extent.maxLng, extent.maxLat];
            // map.current.getView().fit(bounds, { padding: [50, 50, 50, 50] });
        }
        
        // 3. Highlight all monuments
        selectedHistoricMonuments.current = historicMonuments.features;
        highlightSelectedHistoricMonuments(historicMonuments.features);
        
        // 4. Open panel with all monuments
        setHistoricMonumentFeatures(historicMonuments.features);
        setPanelContentType('historicMonuments');
        setPanelOpen(true);
    }, [historicMonuments, highlightSelectedHistoricMonuments]);

    const clearHistoricMonumentHighlight = useCallback(() => {
        selectedHistoricMonuments.current = [];
        setPanelOpen(false);
        setPanelContentType(null);
        setHistoricMonumentFeatures([]);
        
        // Clear visual highlights
        selectedHistoricMonumentFeatures.current.forEach(feature => {
            feature.setStyle(undefined);
        });
        selectedHistoricMonumentFeatures.current = [];
    }, []);

    // Apply highlight styles to specific zones surveillées (called when zones are selected from panel)
    const highlightSelectedZonesSurveillees = useCallback((zones: any[]) => {
        if (!zonesSurveilleesLayer.current || !map.current) return;
        
        // Clear previous highlights for zones surveillées
        selectedZoneSurveilleeFeatures.current.forEach(feature => {
            feature.set('isSelected', false);
        });
        selectedZoneSurveilleeFeatures.current = [];
        
        // Clear previous highlights for historic monuments
        selectedHistoricMonumentFeatures.current.forEach(feature => {
            feature.setStyle(undefined);
        });
        selectedHistoricMonumentFeatures.current = [];
        
        // Find and highlight the selected zones
        const source = zonesSurveilleesLayer.current.getSource();
        if (source) {
            source.forEachFeature((feature: any) => {
                const properties = feature.getProperties();
                const zoneId = properties.CODQUART || properties.CODE_QP || properties.id;
                
                // Check if this zone is in the selected list
                const isSelected = zones.some(zone => {
                    const selectedId = zone.properties?.CODQUART || zone.properties?.CODE_QP || zone.properties?.id;
                    return selectedId === zoneId;
                });
                
                if (isSelected) {
                    // Mark feature as selected and let style function handle the appearance
                    feature.set('isSelected', true);
                    selectedZoneSurveilleeFeatures.current.push(feature);
                }
            });
        }
    }, [zonesSurveilleesLayer, map]);

    // Show all zones surveillées on map and open side panel (called when clicking zone vignette)
    const highlightZonesSurveillees = useCallback(() => {
        if (!zonesSurveillees || !map.current) return;
        
        // Clear previous highlights for heavy rain
        if (selectedHeavyRainFeature.current) {
            selectedHeavyRainFeature.current.setStyle(undefined);
            selectedHeavyRainFeature.current = null;
        }
        
        // Clear previous highlights for historic monuments
        selectedHistoricMonumentFeatures.current.forEach(feature => {
            feature.setStyle(undefined);
        });
        selectedHistoricMonumentFeatures.current = [];
        
        // Collect all zones from different types
        const allZones: any[] = [];
        const zoneTypes = ['qpv', 'qpvAncienDegrade', 'qva', 'zus', 'zfu', 'npnru'];
        
        zoneTypes.forEach(zoneType => {
            if (zonesSurveillees[zoneType] && zonesSurveillees[zoneType].features) {
                zonesSurveillees[zoneType].features.forEach((feature: any) => {
                    allZones.push({
                        ...feature,
                        properties: {
                            ...feature.properties,
                            zoneType: zoneType
                        }
                    });
                });
            }
        });
        if (allZones.length === 0) return;
        
        // 1. Calculate bounds for all zones
        const coordinates = allZones.map((f: any) => f.geometry.coordinates);
        const extent = coordinates.reduce((acc: any, coord: any) => {
            if (coord && coord.length > 0) {
                const [lng, lat] = coord[0]; // First coordinate of polygon
                if (!acc.minLng || lng < acc.minLng) acc.minLng = lng;
                if (!acc.maxLng || lng > acc.maxLng) acc.maxLng = lng;
                if (!acc.minLat || lat < acc.minLat) acc.minLat = lat;
                if (!acc.maxLat || lat > acc.maxLat) acc.maxLat = lat;
            }
            return acc;
        }, {});
        
        // 2. Pan and zoom to show all zones
        if (extent.minLng !== undefined) {
            const bounds = [extent.minLng, extent.minLat, extent.maxLng, extent.maxLat];
            // map.current.getView().fit(bounds, { padding: [50, 50, 50, 50] });
        }
        
        // 3. Highlight all zones
        selectedZonesSurveillees.current = allZones;
        highlightSelectedZonesSurveillees(allZones);
        
        // 4. Open panel with all zones
        setZoneSurveilleeFeatures(allZones);
        setPanelContentType('zonesSurveillees');
        setPanelOpen(true);
    }, [zonesSurveillees, highlightSelectedZonesSurveillees]);

    const clearZoneSurveilleeHighlight = useCallback(() => {
        selectedZonesSurveillees.current = [];
        setPanelOpen(false);
        setPanelContentType(null);
        setZoneSurveilleeFeatures([]);
        
        // Clear visual highlights (layer uses style function + isSelected, not feature.setStyle)
        selectedZoneSurveilleeFeatures.current.forEach(feature => {
            feature.set('isSelected', false);
        });
        selectedZoneSurveilleeFeatures.current = [];
    }, []);

    const centerOnFireStation = useCallback(() => {
        if (!map.current || !fireStations?.features?.length) return;

        const points: number[][] = [];
        const routeCoordinates = fireStations.features[0]?.properties?.route?.coordinates;
        if (Array.isArray(routeCoordinates)) {
            routeCoordinates.forEach((pair: number[]) => {
                if (Array.isArray(pair) && Number.isFinite(pair[0]) && Number.isFinite(pair[1])) {
                    points.push(fromLonLat([pair[0], pair[1]]));
                }
            });
        }
        if (lng != null && lat != null) {
            points.push(fromLonLat([lng, lat]));
        }

        let extent: [number, number, number, number] | null = points.length
            ? boundingExtent(points) as [number, number, number, number]
            : null;

        const stationExtent = fireStationsLayer.current?.getSource()?.getExtent();
        if (stationExtent && Number.isFinite(stationExtent[0])) {
            if (extent) {
                extendExtent(extent, stationExtent);
            } else {
                extent = stationExtent.slice() as [number, number, number, number];
            }
        }
        if (!extent || !Number.isFinite(extent[0])) return;

        map.current.getView().fit(extent, {
            padding: [80, 430, 80, 80],
            maxZoom: 17,
            duration: 400,
        });
        setPanelContentType('fireStation');
        setPanelOpen(true);
    }, [fireStations, lat, lng]);

    useEffect(() => {
        centerOnFireStationRef.current = centerOnFireStation;
    }, [centerOnFireStation]);

    const centerOnGasStation = useCallback(() => {
        if (!map.current || !gasStations?.features?.length) return;

        const points: number[][] = [];
        if (lng != null && lat != null) {
            points.push(fromLonLat([lng, lat]));
        }

        let extent: [number, number, number, number] | null = points.length
            ? boundingExtent(points) as [number, number, number, number]
            : null;

        const stationExtent = gasStationsLayer.current?.getSource()?.getExtent();
        if (stationExtent && Number.isFinite(stationExtent[0])) {
            if (extent) {
                extendExtent(extent, stationExtent);
            } else {
                extent = stationExtent.slice() as [number, number, number, number];
            }
        }
        if (!extent || !Number.isFinite(extent[0])) return;

        map.current.getView().fit(extent, {
            padding: [80, 430, 80, 80],
            maxZoom: 17,
            duration: 400,
        });
        setPanelContentType('gasStation');
        setPanelOpen(true);
    }, [gasStations, lat, lng]);

    useEffect(() => {
        centerOnGasStationRef.current = centerOnGasStation;
    }, [centerOnGasStation]);

    useEffect(() => {
        if (!map.current) return;

        const removeRoute = () => {
            if (fireStationRouteLayer.current) {
                map.current?.removeLayer(fireStationRouteLayer.current);
                fireStationRouteLayer.current = null;
            }
        };

        const showRoute = panelOpen && panelContentType === 'fireStation';
        const routeCoordinates = fireStations?.features?.[0]?.properties?.route?.coordinates;
        if (!showRoute || !Array.isArray(routeCoordinates) || routeCoordinates.length < 2) {
            removeRoute();
            return;
        }

        removeRoute();
        const feature = new Feature({
            geometry: new LineString(
                routeCoordinates
                    .filter((pair: number[]) => Array.isArray(pair) && Number.isFinite(pair[0]) && Number.isFinite(pair[1]))
                    .map((pair: number[]) => fromLonLat([pair[0], pair[1]]))
            ),
        });
        const layer = new VectorLayer({
            source: new VectorSource({ features: [feature] }),
            style: new Style({
                stroke: new Stroke({
                    color: MAP_COLORS.fireStations.stroke,
                    width: 4,
                }),
            }),
            zIndex: 17,
        });
        fireStationRouteLayer.current = layer;
        map.current.addLayer(layer);

        return removeRoute;
    }, [panelOpen, panelContentType, fireStations]);

    const restorePortfolioHomeView = useCallback(() => {
        const home = portfolioHomeView.current;
        portfolioHomeView.current = null;
        if (!map.current || !home) return;
        map.current.getView().animate({
            center: home.center,
            zoom: home.zoom,
            duration: 400,
        });
    }, []);

    // Expose functions via ref
    useImperativeHandle(ref, () => ({
        highlightHistoricMonuments,
        clearHistoricMonumentHighlight,
        highlightZonesSurveillees,
        clearZoneSurveilleeHighlight,
        centerOnFireStation,
        restorePortfolioHomeView,
        centerOnGasStation,
    }), [highlightHistoricMonuments, clearHistoricMonumentHighlight, highlightZonesSurveillees, clearZoneSurveilleeHighlight, centerOnFireStation, restorePortfolioHomeView, centerOnGasStation]);

    // Initialize the map
    useEffect(() => {
        if (mapElement.current && !map.current) {
            const osmTileLayer = new TileLayer({
                preload: Infinity,
                className: 'ol_bw',
                source: new OSM(),
            })

            osmLayer.current = osmTileLayer

            const initialMap = new Map({
                target: mapElement.current || undefined,
                layers: [osmTileLayer],
                view: new View({
                  center: fromLonLat([2.333333, 48.866667]),
                  zoom: 10,
                  // projection: "EPSG:4326"
                }),
                // view: new View({
                //   center: [0, 0],
                //   zoom: 2,
                // }),
                // pixelRatio: 1
              });
            map.current = initialMap

            // // Add custom controls
            // if (onMenuClick) {
            //     const menuControl = new MenuControl(onMenuClick);
            //     initialMap.addControl(menuControl);
            // }
            
            // if (onLayersClick) {
            //     const layersControl = new LayersControl(onLayersClick);
            //     initialMap.addControl(layersControl);
            // }
            
            // if (onInfoClick) {
            //     const infoControl = new InfoControl(onInfoClick);
            //     initialMap.addControl(infoControl);
            // }

            // Add measure control
            const measureCtrl = new MeasureControl(() => setIsMeasureMode(!isMeasureMode));
            measureControl.current = measureCtrl;
            initialMap.addControl(measureCtrl);

            if (onCoordinatesSelected) {
                const gpsPickCtrl = new GpsPickControl(() => setIsGpsPickMode(prev => !prev));
                gpsPickControl.current = gpsPickCtrl;
                initialMap.addControl(gpsPickCtrl);
            }

            // Add measure info control
            const measureInfoCtrl = new MeasureInfoControl();
            measureInfoControl.current = measureInfoCtrl;
            initialMap.addControl(measureInfoCtrl);

            // Add close button handler for measure
            const closeButton = measureInfoCtrl.getInfoPanel()?.querySelector('#measure-close');
            if (closeButton) {
                closeButton.addEventListener('click', () => {
                    setIsMeasureMode(false);
                });
            }

            initialMap.on('loadstart', function () {
                initialMap.getTargetElement().classList.add('spinner');
            });
            initialMap.on('loadend', function () {
                initialMap.getTargetElement().classList.remove('spinner');
            });
            
            // for debug, do not forget to remove
            (window as any).mapObject = initialMap;
        }

    }, [mapElement, onMenuClick, onLayersClick, onInfoClick, isMeasureMode]);

    // handle center and zoom changes
    useEffect(() => {
      if (!map.current) { return }
      
      // Use default values if not provided
      const currentCenter = center || { lat: 48.866667, lng: 2.333333 };
      const currentZoom = zoom || 10;
      
      map.current.setView(new View({
          center: fromLonLat([currentCenter.lng, currentCenter.lat]),
          zoom: currentZoom,
      }))

      const iconFeature = new Feature({
          geometry: new Point(fromLonLat([currentCenter.lng, currentCenter.lat])),
          name: 'Location',
      });

      const iconStyle = new Style({
          image: new Icon({
              anchor: [0.4, 1],
              anchorXUnits: 'fraction',
              anchorYUnits: 'fraction',
              src: mapMarker,
          }),
          stroke: new Stroke({
            color: MAP_COLORS.historicMonuments.color,
            width: 3
        })
      });

      iconFeature.setStyle(iconStyle);

      const vectorSource = new VectorSource({
          features: [iconFeature],
      });

      const vectorLayer = new VectorLayer({
          source: vectorSource,
          zIndex: 30,
      });

      markerLayer.current = vectorLayer
      map.current?.addLayer(markerLayer.current)

      return () => {
          if (markerLayer.current) {
              map.current?.removeLayer(markerLayer.current)
              markerLayer.current = null
          }
      }
    }, [center, zoom])
    
    // Handle building and parcel layers (/place) — masqués sur l'onglet Copropriété (on affiche buildingGroup à la place)
    useEffect(() => {
        if (!map.current) return;
        if (mapDataMode !== 'place') {
            if (buildingLayer.current) {
                map.current.removeLayer(buildingLayer.current);
                buildingLayer.current = null;
            }
            if (parcelLayer.current) {
                map.current.removeLayer(parcelLayer.current);
                parcelLayer.current = null;
            }
            return;
        }

        // Add parcel data (onglet Infos générales uniquement)
        if (data4HomeInfo?.parcels && data4HomeInfo.parcels.features && data4HomeInfo.parcels.features.length > 0) {
            
            // Validate GeoJSON structure
            if (!data4HomeInfo.parcels.type || data4HomeInfo.parcels.type !== 'FeatureCollection') {
                console.error('OLMap - Invalid GeoJSON structure for parcels:', data4HomeInfo.parcels);
            } else {
                try {
                    const parcelSource = new VectorSource({
                        features: new GeoJSON().readFeatures(data4HomeInfo.parcels, {
                            featureProjection: 'EPSG:3857'
                        })
                    });

                    const parcelVectorLayer = new VectorLayer({
                        source: parcelSource,
                        className: 'parcel-layer',
                        style: new Style({
                            fill: new Fill({ color: MAP_COLORS.parcels.fill }),
                            stroke: new Stroke({ 
                                color: MAP_COLORS.parcels.stroke, 
                                width: MAP_COLORS.parcels.width 
                            })
                        }),
                        zIndex: 13,
                    });

                    parcelLayer.current = parcelVectorLayer;
                    map.current.addLayer(parcelVectorLayer);
                } catch (error) {
                    console.error('OLMap - Error processing parcels:', error);
                    console.error('OLMap - Parcels data:', data4HomeInfo.parcels);
                }
            }
        }

        // Add building data (onglet Infos générales uniquement)
        if (data4HomeInfo?.buildings && data4HomeInfo.buildings.features && data4HomeInfo.buildings.features.length > 0) {
            
            // Validate GeoJSON structure
            if (!data4HomeInfo.buildings.type || data4HomeInfo.buildings.type !== 'FeatureCollection') {
                console.error('OLMap - Invalid GeoJSON structure for buildings:', data4HomeInfo.buildings);
            } else {
                try {
                    const buildingSource = new VectorSource({
                        features: new GeoJSON().readFeatures(data4HomeInfo.buildings, {
                            featureProjection: 'EPSG:3857'
                        })
                    });

                    const buildingVectorLayer = new VectorLayer({
                        source: buildingSource,
                        className: 'building-layer',
                        style: new Style({
                            fill: new Fill({ color: MAP_COLORS.buildings.fill }),
                            stroke: new Stroke({ 
                                color: MAP_COLORS.buildings.stroke, 
                                width: MAP_COLORS.buildings.width 
                            })
                        }),
                        zIndex: 13,
                    });

                    buildingLayer.current = buildingVectorLayer;
                    map.current.addLayer(buildingVectorLayer);
                } catch (error) {
                    console.error('OLMap - Error processing buildings:', error);
                    console.error('OLMap - Buildings data:', data4HomeInfo.buildings);
                }
            }
        }

        return () => {
            if (buildingLayer.current) {
                map.current?.removeLayer(buildingLayer.current);
                buildingLayer.current = null;
            }
            if (parcelLayer.current) {
                map.current?.removeLayer(parcelLayer.current);
                parcelLayer.current = null;
            }
        };
    }, [data4HomeInfo, mapDataMode]);

    // Place-copro : geometry buildingGroup (groupe de bâtiment) + parcelles cadastre de la copro sélectionnée (jaune) + position GPS copro
    useEffect(() => {
        if (!map.current || mapDataMode !== 'placeCopro' || !placeCoproData) return;

        const format = new GeoJSON();
        const layersToRemove: React.MutableRefObject<any | null>[] = [
            placeCoproBuildingGroupLayer,
            placeCoproParcellesCoproLayer,
            placeCoproMarkerLayer
        ];

        // 0) Groupe de bâtiment BDNB (geometry de buildingGroup) — remplace le bâtiment/parcelle de /place
        const buildingGroup = placeCoproData.buildingGroup;
        if (buildingGroup?.geometry && buildingGroup.type === 'Feature') {
            try {
                const features = format.readFeatures(
                    { type: 'Feature', geometry: buildingGroup.geometry, properties: buildingGroup.properties ?? {} },
                    { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
                );
                if (features.length > 0) {
                    const layer = new VectorLayer({
                        source: new VectorSource({ features }),
                        style: new Style({
                            fill: new Fill({ color: MAP_COLORS.placeCoproBuildingAtLocation.fill }),
                            stroke: new Stroke({ color: MAP_COLORS.placeCoproBuildingAtLocation.stroke, width: 2 })
                        }),
                        zIndex: 12,
                        className: 'place-copro-building-group-layer'
                    });
                    placeCoproBuildingGroupLayer.current = layer;
                    map.current.addLayer(layer);
                }
            } catch (e) {
                console.warn('OLMap - placeCopro buildingGroup:', e);
            }
        }

        // 1) Parcelles cadastre de la copro sélectionnée (direct ou nearbyCopro) — en jaune
        const copros = placeCoproData.copros ?? [];
        const nearbyCopro = placeCoproData.nearbyCopro;
        const selectedIndex = placeCoproSelectedIndex ?? 0;
        const selectedCopro =
          nearbyCopro?.properties && selectedIndex >= copros.length
            ? nearbyCopro.properties
            : copros[Math.min(Math.max(0, selectedIndex), copros.length - 1)];
        const parcellesCopro = selectedCopro?.parcelles_cadastre ?? [];
        if (parcellesCopro.length > 0) {
            const features: any[] = [];
            for (const p of parcellesCopro) {
                if (!p?.geometry?.type) continue;
                const feats = format.readFeatures(
                    { type: 'Feature', geometry: p.geometry, properties: { id: p.id } },
                    { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
                );
                features.push(...feats);
            }
            if (features.length > 0) {
                const layer = new VectorLayer({
                    source: new VectorSource({ features }),
                    style: new Style({
                        fill: new Fill({ color: MAP_COLORS.placeCoproParcellesCadastreCopro.fill }),
                        stroke: new Stroke({ color: MAP_COLORS.placeCoproParcellesCadastreCopro.stroke, width: MAP_COLORS.placeCoproParcellesCadastreCopro.width })
                    }),
                    zIndex: 15,
                    className: 'place-copro-parcelles-copro-layer'
                });
                placeCoproParcellesCoproLayer.current = layer;
                map.current.addLayer(layer);
            }
        }

        // 2) Position GPS de la copro sélectionnée (lat, long) ou geometry de la Feature nearby
        let coproLat = selectedCopro?.lat != null ? Number(selectedCopro.lat) : NaN;
        let coproLong = selectedCopro?.long != null ? Number(selectedCopro.long) : NaN;
        if (selectedIndex >= copros.length && nearbyCopro?.geometry && Array.isArray((nearbyCopro.geometry as any).coordinates) && (nearbyCopro.geometry as any).coordinates.length >= 2) {
          const coords = (nearbyCopro.geometry as { coordinates: number[] }).coordinates;
          coproLat = Number(coords[1]);
          coproLong = Number(coords[0]);
        }
        if (!isNaN(coproLat) && !isNaN(coproLong)) {
            try {
                const point = new Point(fromLonLat([coproLong, coproLat]));
                const feature = new Feature({ geometry: point });
                const layer = new VectorLayer({
                    source: new VectorSource({ features: [feature] }),
                    style: new Style({
                        image: new CircleStyle({
                            radius: 8,
                            fill: new Fill({ color: MAP_COLORS.placeCoproPositionMarker.fill }),
                            stroke: new Stroke({ color: MAP_COLORS.placeCoproPositionMarker.stroke, width: MAP_COLORS.placeCoproPositionMarker.width })
                        })
                    }),
                    zIndex: 17,
                    className: 'place-copro-marker-layer'
                });
                placeCoproMarkerLayer.current = layer;
                map.current.addLayer(layer);
            } catch (e) {
                console.error('OLMap - placeCopro marker:', e);
            }
        }

        return () => {
            layersToRemove.forEach((ref) => {
                if (ref.current) {
                    map.current?.removeLayer(ref.current);
                    ref.current = null;
                }
            });
        };
    }, [mapDataMode, placeCoproData, placeCoproSelectedIndex]);

    // Portefeuille : points de la page courante tant que le tiroir est ouvert, plus le losange de la ligne cliquée.
    useEffect(() => {
        const removePortfolioLayer = () => {
            if (placeCoproPortfolioLayer.current) {
                map.current?.removeLayer(placeCoproPortfolioLayer.current);
                placeCoproPortfolioLayer.current = null;
            }
        };

        if (!map.current || mapDataMode !== 'placeCopro' || !portfolioPoints || portfolioPoints.length === 0) {
            removePortfolioLayer();
            lastPortfolioFocusId.current = null;
            // Tiroir fermé sans restauration (autre adresse, autre onglet) : on oublie la vue.
            if (!portfolioPoints) portfolioHomeView.current = null;
            return;
        }

        removePortfolioLayer();

        let focus: { lat: number; long: number } | null = null;
        const features: Feature[] = [];
        for (const point of portfolioPoints) {
            const lat = Number(point.lat);
            const lon = Number(point.long);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
            const selected = portfolioSelectedId != null && point.id === portfolioSelectedId;
            if (selected) focus = { lat, long: lon };
            const feature = new Feature({
                geometry: new Point(fromLonLat([lon, lat])),
            });
            feature.setStyle(selected ? PORTFOLIO_SELECTED_STYLE : PORTFOLIO_POINT_STYLE);
            features.push(feature);
        }

        if (features.length > 0) {
            const layer = new VectorLayer({
                source: new VectorSource({ features }),
                zIndex: 18,
                className: 'place-copro-portfolio-layer',
            });
            placeCoproPortfolioLayer.current = layer;
            map.current.addLayer(layer);
        }

        if (focus && portfolioSelectedId && portfolioSelectedId !== lastPortfolioFocusId.current) {
            lastPortfolioFocusId.current = portfolioSelectedId;
            const view = map.current.getView();
            if (!portfolioHomeView.current) {
                const center = view.getCenter();
                if (center) portfolioHomeView.current = { center: [...center], zoom: view.getZoom() };
            }
            const zoom = view.getZoom() ?? 16;
            view.animate({
                center: fromLonLat([focus.long, focus.lat]),
                zoom: zoom < 14 ? 16 : zoom,
                duration: 400,
            });
        }
        if (!portfolioSelectedId) {
            lastPortfolioFocusId.current = null;
        }

        return () => {
            removePortfolioLayer();
        };
    }, [mapDataMode, portfolioPoints, portfolioSelectedId]);

    // Handle heavy rain layer
    useEffect(() => {
      if (!map.current) return;

      // Add heavy rain data
      if (heavyRain && heavyRain.features && heavyRain.features.length > 0) {
          
          // Validate GeoJSON structure
          if (!heavyRain.type ||heavyRain.type !== 'FeatureCollection') {
              console.error('OLMap - Invalid GeoJSON structure for heavy rain:', heavyRain);
          } else {
              try {
                  // Read features and filter duplicates based on coordinates
                  const allFeatures = new GeoJSON().readFeatures(heavyRain, {
                      featureProjection: 'EPSG:3857'
                  });
                  
                  // Filter duplicates based on coordinates
                  const uniqueFeatures: { [key: string]: any } = {};
                  const filteredFeatures: any[] = [];
                  
                  allFeatures.forEach((feature: any) => {
                      const geometry = feature.getGeometry();
                      if (geometry && geometry.getType() === 'Point') {
                          const coordinates = geometry.getCoordinates();
                          const coordKey = `${coordinates[0]},${coordinates[1]}`;
                          if (!uniqueFeatures[coordKey]) {
                              uniqueFeatures[coordKey] = true;
                              filteredFeatures.push(feature);
                          }
                      }
                  });
                  
                  const heavyRainSource = new VectorSource({
                      features: filteredFeatures
                  });

                  const heavyRainVectorLayer = new VectorLayer({
                      source: heavyRainSource,
                      className: 'heavy-rain-layer',
                      style: new Style({
                        image: new CircleStyle({
                          radius: MAP_COLORS.heavyRain.radius,
                          fill: new Fill({ color: MAP_COLORS.heavyRain.fill }),
                          stroke: new Stroke({ 
                            color: MAP_COLORS.heavyRain.stroke, 
                            width: MAP_COLORS.heavyRain.strokeWidth 
                          })
                        })
                      }),
                      zIndex: 14,
                  });

                  heavyRainLayer.current = heavyRainVectorLayer;
                  map.current.addLayer(heavyRainVectorLayer);

                  // Add hover handler for cursor change
                  const handleHeavyRainHover = (event: any) => {
                      if (isMeasureMode || isGpsPickMode) return; // Don't change cursor in measure or GPS pick mode
                      
                      let featureFound = false;
                      
                      // Use forEachFeatureAtPixel for more reliable feature detection
                      map.current?.forEachFeatureAtPixel(event.pixel, (feature: any, layer: any) => {
                          // Check if the feature belongs to the heavy rain layer
                          if (layer === heavyRainVectorLayer) {
                              featureFound = true;
                              return true; // Stop iteration after finding the first feature
                          }
                      }, {
                          layerFilter: (layer: any) => layer === heavyRainVectorLayer,
                          hitTolerance: 5 // Allow some tolerance for easier hovering
                      });
                      
                      // Update cursor based on feature presence
                      if (map.current) {
                          map.current.getViewport().style.cursor = featureFound ? 'pointer' : '';
                      }
                  };


                  map.current.on('pointermove', handleHeavyRainHover);

                  // Store the handlers for cleanup
                  (heavyRainVectorLayer as any)._hoverHandler = handleHeavyRainHover;
              } catch (error) {
                  console.error('OLMap - Error processing heavy rain:', error);
                  console.error('OLMap - heavy rain data:', heavyRain);
              }
          }
      }

      return () => {
          if (heavyRainLayer.current) {
              // Remove handlers before removing layer
              const hoverHandler = (heavyRainLayer.current as any)._hoverHandler;
              if (map.current && hoverHandler) {
                  map.current.un('pointermove', hoverHandler);
              }
              // Clear selected feature style if any
              if (selectedHeavyRainFeature.current) {
                  selectedHeavyRainFeature.current.setStyle(undefined);
                  selectedHeavyRainFeature.current = null;
              }
              
              map.current?.removeLayer(heavyRainLayer.current);
              heavyRainLayer.current = null;
          }
      };
  }, [heavyRain, isMeasureMode, isGpsPickMode]);

    // Handle tree features returned with GET /place (trees).
    useEffect(() => {
        if (!map.current || !trees) return;

        if (trees.type !== 'FeatureCollection') {
            console.error('OLMap - Invalid GeoJSON structure for trees:', trees);
            return;
        }

        try {
            const source = new VectorSource({
                features: new GeoJSON().readFeatures(trees, {
                    featureProjection: 'EPSG:3857'
                })
            });
            const layer = new VectorLayer({
                source,
                className: 'trees-layer',
                style: new Style({
                    image: new CircleStyle({
                        radius: MAP_COLORS.trees.radius,
                        fill: new Fill({ color: MAP_COLORS.trees.fill }),
                        stroke: new Stroke({
                            color: MAP_COLORS.trees.stroke,
                            width: MAP_COLORS.trees.strokeWidth
                        })
                    })
                }),
                zIndex: 15
            });

            treesLayer.current = layer;
            map.current.addLayer(layer);
        } catch (error) {
            console.error('OLMap - Error processing trees:', error);
        }

        return () => {
            if (treesLayer.current) {
                map.current?.removeLayer(treesLayer.current);
                treesLayer.current = null;
            }
        };
    }, [trees]);

    useEffect(() => {
        if (!map.current || !fireStations) return;

        if (fireStations.type !== 'FeatureCollection') {
            console.error('OLMap - Invalid GeoJSON structure for fire stations:', fireStations);
            return;
        }

        try {
            const source = new VectorSource({
                features: new GeoJSON().readFeatures(fireStations, {
                    featureProjection: 'EPSG:3857'
                })
            });
            // Polygone masqué : décommenter pour réafficher l'emprise de la caserne.
            // const polygonStyle = new Style({
            //     fill: new Fill({ color: MAP_COLORS.fireStations.fill }),
            //     stroke: new Stroke({
            //         color: MAP_COLORS.fireStations.stroke,
            //         width: MAP_COLORS.fireStations.width
            //     })
            // });
            const layer = new VectorLayer({
                source,
                className: 'fire-stations-layer',
                style: (feature) => {
                    const geometry = feature.getGeometry();
                    const styles: Style[] = [];
                    // styles.push(polygonStyle);
                    let center = null;
                    if (geometry instanceof Polygon) {
                        center = geometry.getInteriorPoint();
                    } else if (geometry instanceof MultiPolygon) {
                        const polygons = geometry.getPolygons();
                        if (polygons.length > 0) {
                            const largest = polygons.reduce((best, polygon) =>
                                polygon.getArea() > best.getArea() ? polygon : best
                            );
                            center = largest.getInteriorPoint();
                        }
                    }
                    if (center) {
                        styles.push(new Style({
                            geometry: center,
                            image: new Icon({
                                src: fireTruckIcon,
                                anchor: [0.5, 0.5],
                                anchorXUnits: 'fraction',
                                anchorYUnits: 'fraction',
                            })
                        }));
                    }
                    return styles;
                },
                zIndex: 18
            });
            fireStationsLayer.current = layer;
            map.current.addLayer(layer);

            const handleFireStationHover = (event: any) => {
                if (isMeasureMode || isGpsPickMode) return;

                let featureFound = false;
                map.current?.forEachFeatureAtPixel(event.pixel, (_feature: any, hoveredLayer: any) => {
                    if (hoveredLayer === layer) {
                        featureFound = true;
                        return true;
                    }
                }, {
                    layerFilter: (hoveredLayer: any) => hoveredLayer === layer,
                    hitTolerance: 5
                });

                if (map.current) {
                    map.current.getViewport().style.cursor = featureFound ? 'pointer' : '';
                }
            };

            map.current.on('pointermove', handleFireStationHover);
            (layer as any)._hoverHandler = handleFireStationHover;
        } catch (error) {
            console.error('OLMap - Error processing fire stations:', error);
        }

        return () => {
            if (fireStationsLayer.current) {
                const hoverHandler = (fireStationsLayer.current as any)._hoverHandler;
                if (map.current && hoverHandler) {
                    map.current.un('pointermove', hoverHandler);
                }
                map.current?.removeLayer(fireStationsLayer.current);
                fireStationsLayer.current = null;
            }
        };
    }, [fireStations, isMeasureMode, isGpsPickMode]);

    useEffect(() => {
        if (!map.current || !gasStations) return;

        if (gasStations.type !== 'FeatureCollection') {
            console.error('OLMap - Invalid GeoJSON structure for gas stations:', gasStations);
            return;
        }

        try {
            const source = new VectorSource({
                features: new GeoJSON().readFeatures(gasStations, {
                    featureProjection: 'EPSG:3857'
                })
            });
            const layer = new VectorLayer({
                source,
                className: 'gas-stations-layer',
                style: (feature) => {
                    const geometry = feature.getGeometry();
                    if (!(geometry instanceof Point)) {
                        return undefined;
                    }
                    return new Style({
                        image: new Icon({
                            src: gasStationIcon,
                            anchor: [0.5, 0.5],
                            anchorXUnits: 'fraction',
                            anchorYUnits: 'fraction',
                        })
                    });
                },
                zIndex: 19
            });
            gasStationsLayer.current = layer;
            map.current.addLayer(layer);

            const handleGasStationHover = (event: any) => {
                if (isMeasureMode || isGpsPickMode) return;

                let featureFound = false;
                map.current?.forEachFeatureAtPixel(event.pixel, (_feature: any, hoveredLayer: any) => {
                    if (hoveredLayer === layer) {
                        featureFound = true;
                        return true;
                    }
                }, {
                    layerFilter: (hoveredLayer: any) => hoveredLayer === layer,
                    hitTolerance: 5
                });

                if (map.current) {
                    map.current.getViewport().style.cursor = featureFound ? 'pointer' : '';
                }
            };

            map.current.on('pointermove', handleGasStationHover);
            (layer as any)._hoverHandler = handleGasStationHover;
        } catch (error) {
            console.error('OLMap - Error processing gas stations:', error);
        }

        return () => {
            if (gasStationsLayer.current) {
                const hoverHandler = (gasStationsLayer.current as any)._hoverHandler;
                if (map.current && hoverHandler) {
                    map.current.un('pointermove', hoverHandler);
                }
                map.current?.removeLayer(gasStationsLayer.current);
                gasStationsLayer.current = null;
            }
        };
    }, [gasStations, isMeasureMode, isGpsPickMode]);

    // Handle water ways layer
    useEffect(() => {
        if (!map.current || !waterWays) return;

        // Add water ways data
        if (waterWays) {
            // Extract waterWays data from nested structure
            const waterWaysData = waterWays;
            
            // Validate GeoJSON structure
            if (!waterWaysData.type || waterWaysData.type !== 'FeatureCollection') {
                console.error('OLMap - Invalid GeoJSON structure for water ways:', waterWaysData);
            } else {
                try {
                    const waterWaysSource = new VectorSource({
                        features: new GeoJSON().readFeatures(waterWaysData, {
                            featureProjection: 'EPSG:3857'
                        })
                    });

                    const waterWaysVectorLayer = new VectorLayer({
                        source: waterWaysSource,
                        className: 'water-ways-layer',
                        style: new Style({
                            fill: new Fill({ color: 'rgba(51, 100, 255, 0.3)' }),
                            stroke: new Stroke({ 
                                color: MAP_COLORS.waterWays.stroke, 
                                width: MAP_COLORS.waterWays.width 
                            })
                        }),
                        zIndex: 12,
                    });

                    waterWaysLayer.current = waterWaysVectorLayer;
                    map.current.addLayer(waterWaysVectorLayer);

                    // Add marker for closest point
                    if (waterWays.closest && waterWays.closest.point) {
                        const closestPointFeature = new Feature({
                            geometry: new Point(fromLonLat(waterWays.closest.point)),
                            name: 'Closest Water Way'
                        });

                        const closestPointStyle = new Style({
                            image: new CircleStyle({
                                radius: MAP_COLORS.waterWays.closestPoint.radius,
                                fill: new Fill({ color: MAP_COLORS.waterWays.closestPoint.fill }),
                                stroke: new Stroke({ 
                                    color: MAP_COLORS.waterWays.closestPoint.stroke, 
                                    width: MAP_COLORS.waterWays.closestPoint.strokeWidth 
                                })
                            })
                        });

                        closestPointFeature.setStyle(closestPointStyle);

                        const closestPointSource = new VectorSource({
                            features: [closestPointFeature],
                        });

                        const closestPointVectorLayer = new VectorLayer({
                            source: closestPointSource,
                            className: 'closest-point-layer',
                            zIndex: 12,
                        });

                        closestPointLayer.current = closestPointVectorLayer;
                        map.current.addLayer(closestPointVectorLayer);
                    }
                } catch (error) {
                    console.error('OLMap - Error processing water ways:', error);
                    console.error('OLMap - Water ways data:', waterWays);
                }
            }
        }

        return () => {
            if (waterWaysLayer.current) {
                map.current?.removeLayer(waterWaysLayer.current);
                waterWaysLayer.current = null;
            }
            if (closestPointLayer.current) {
                map.current?.removeLayer(closestPointLayer.current);
                closestPointLayer.current = null;
            }
        };
    }, [waterWays]);

    // Handle rail roads layer
    useEffect(() => {
        if (!map.current || !railRoads) return;

        // Add rail roads data
        if (railRoads) {
            // Extract railRoads data from nested structure
            const railRoadsData = railRoads;
            
            // Validate GeoJSON structure
            if (!railRoadsData.type || railRoadsData.type !== 'FeatureCollection') {
                console.error('OLMap - Invalid GeoJSON structure for rail roads:', railRoadsData);
            } else {
                try {
                    const railRoadsSource = new VectorSource({
                        features: new GeoJSON().readFeatures(railRoadsData, {
                            featureProjection: 'EPSG:3857'
                        })
                    });

                    const railRoadsVectorLayer = new VectorLayer({
                        source: railRoadsSource,
                        className: 'rail-roads-layer',
                        style: new Style({
                            stroke: new Stroke({ 
                                color: MAP_COLORS.railRoads.stroke,
                                width: MAP_COLORS.railRoads.width
                            })
                        }),
                        zIndex: 11,
                    });

                    railRoadsLayer.current = railRoadsVectorLayer;
                    map.current.addLayer(railRoadsVectorLayer);

                    // Add marker for closest rail road point
                    if (railRoads.closest && railRoads.closest.point) {
                        const closestRailRoadPointFeature = new Feature({
                            geometry: new Point(fromLonLat(railRoads.closest.point)),
                            name: 'Closest Rail Road'
                        });

                        const closestRailRoadPointStyle = new Style({
                            image: new CircleStyle({
                                radius: 6,
                                fill: new Fill({ color: MAP_COLORS.railRoads.stroke }),
                                stroke: new Stroke({ color: '#ffffff', width: 2 })
                            })
                        });

                        closestRailRoadPointFeature.setStyle(closestRailRoadPointStyle);

                        const closestRailRoadPointSource = new VectorSource({
                            features: [closestRailRoadPointFeature],
                        });

                        const closestRailRoadPointVectorLayer = new VectorLayer({
                            source: closestRailRoadPointSource,
                            className: 'closest-rail-road-point-layer',
                            zIndex: 12,
                        });

                        // Store reference to the closest rail road point layer
                        (railRoadsLayer.current as any).closestPointLayer = closestRailRoadPointVectorLayer;
                        map.current.addLayer(closestRailRoadPointVectorLayer);
                    }

                } catch (error) {
                    console.error('OLMap - Error creating rail roads layer:', error);
                }
            }
        }

        return () => {
            if (railRoadsLayer.current && map.current) {
                // Remove the closest point layer if it exists
                const closestPointLayer = (railRoadsLayer.current as any).closestPointLayer;
                if (closestPointLayer) {
                    map.current.removeLayer(closestPointLayer);
                }
                
                map.current.removeLayer(railRoadsLayer.current);
                railRoadsLayer.current = null;
            }
        };
    }, [railRoads]);

    // Handle historic monuments layer
    useEffect(() => {
        if (!map.current || !historicMonuments) return;

        // Add historic monuments data
        if (historicMonuments && historicMonuments.features && historicMonuments.features.length > 0) {
            
            // Validate GeoJSON structure
            if (!historicMonuments.type || historicMonuments.type !== 'FeatureCollection') {
                console.error('OLMap - Invalid GeoJSON structure for historic monuments:', historicMonuments);
            } else {
                try {
                    const historicMonumentsSource = new VectorSource({
                        features: new GeoJSON().readFeatures(historicMonuments, {
                            featureProjection: 'EPSG:3857'
                        })
                    });

                    const historicMonumentsVectorLayer = new VectorLayer({
                        source: historicMonumentsSource,
                        className: 'historic-monuments-layer',
                        style: new Style({
                            image: new Icon({
                                src: monumentIcon,
                                anchor: [0.5, 0.5], // Anchor at center
                                anchorXUnits: 'fraction',
                                anchorYUnits: 'fraction',
                                scale: 1,
                                color: MAP_COLORS.historicMonuments.color // Purple color
                            })
                        }),
                        zIndex: 16,
                    });

                    historicMonumentsLayer.current = historicMonumentsVectorLayer;
                    map.current.addLayer(historicMonumentsVectorLayer);

                    // Add hover handler for cursor change
                    const handleHistoricMonumentHover = (event: any) => {
                        if (isMeasureMode || isGpsPickMode) return; // Don't change cursor in measure or GPS pick mode

                        let featureFound = false;

                        // Use forEachFeatureAtPixel for more reliable feature detection
                        map.current?.forEachFeatureAtPixel(event.pixel, (feature: any, layer: any) => {
                            // Check if the feature belongs to the historic monuments layer
                            if (layer === historicMonumentsVectorLayer) {
                                featureFound = true;
                                return true; // Stop iteration after finding the first feature
                            }
                        }, {
                            layerFilter: (layer: any) => layer === historicMonumentsVectorLayer,
                            hitTolerance: 5 // Allow some tolerance for easier hovering
                        });
                        
                        // Update cursor based on feature presence
                        if (map.current) {
                            map.current.getViewport().style.cursor = featureFound ? 'pointer' : '';
                        }
                    };

                    map.current.on('pointermove', handleHistoricMonumentHover);

                    // Store the handlers for cleanup
                    (historicMonumentsVectorLayer as any)._hoverHandler = handleHistoricMonumentHover;
                } catch (error) {
                    console.error('OLMap - Error processing historic monuments:', error);
                    console.error('OLMap - Historic monuments data:', historicMonuments);
                }
            }
        }

        return () => {
            if (historicMonumentsLayer.current) {
                // Remove handlers before removing layer
                const hoverHandler = (historicMonumentsLayer.current as any)._hoverHandler;
                if (map.current && hoverHandler) {
                    map.current.un('pointermove', hoverHandler);
                }
                
                map.current?.removeLayer(historicMonumentsLayer.current);
                historicMonumentsLayer.current = null;
            }
        };
    }, [historicMonuments, isMeasureMode, isGpsPickMode]);

    // Handle zones surveillées layer
    useEffect(() => {
        if (!map.current || !zonesSurveillees) return;

        // Add zones surveillées data
        if (zonesSurveillees) {
            // Collect all zones from different types
            const allZones: any[] = [];
            const zoneTypes = ['qpv', 'qpvAncienDegrade', 'qva', /*'zus', 'zfu',*/ 'npnru'];
            
            zoneTypes.forEach(zoneType => {
                if (zonesSurveillees[zoneType] && zonesSurveillees[zoneType].features) {
                    zonesSurveillees[zoneType].features.forEach((feature: any) => {
                        allZones.push({
                            ...feature,
                            properties: {
                                ...feature.properties,
                                zoneType: zoneType
                            }
                        });
                    });
                }
            });
            
            if (allZones.length > 0) {
                // Create a combined GeoJSON feature collection
                const combinedGeoJSON = {
                    type: 'FeatureCollection',
                    features: allZones
                };
                
                try {
                    const zonesSurveilleesSource = new VectorSource({
                        features: new GeoJSON().readFeatures(combinedGeoJSON, {
                            featureProjection: 'EPSG:3857'
                        })
                    });

                    /**
                     * STYLE FUNCTION FOR ZONES SURVEILLÉES
                     * =====================================
                     * 
                     * This function manages the visual appearance of zones surveillées based on their state.
                     * It implements a sophisticated styling system that:
                     * 
                     * 1. STATE-BASED STYLING
                     *    - Normal state: Light pattern with subtle stroke
                     *    - Selected state: Darker pattern with prominent stroke
                     * 
                     * 2. PATTERN SYSTEM INTEGRATION
                     *    - Uses createHatchingPattern() with theme-based parameters
                     *    - Different colors, line widths, and spacing for each state
                     *    - Maintains visual consistency across the application
                     * 
                     * 3. Z-INDEX ORDERING
                     *    - Selected features (zIndex: 1) appear above normal features (zIndex: 0)
                     *    - Ensures selected zones are always visible
                     *    - Respects layer hierarchy (doesn't exceed parent layer zIndex)
                     * 
                     * 4. THEME INTEGRATION
                     *    - All colors and parameters come from MAP_COLORS constant
                     *    - Easy to modify appearance by updating theme configuration
                     *    - Consistent with other map elements
                     */
                    const zonesSurveilleesStyleFunction = (feature: any) => {
                        // Check if this feature is currently selected
                        const isSelected = feature.get('isSelected') || false;
                        
                        return new Style({
                            // PATTERN FILL: Diagonal hatching with state-based parameters
                            fill: new Fill({ 
                                color: createHatchingPattern(
                                    // Pattern color: darker for selected state
                                    isSelected 
                                        ? MAP_COLORS.zonesSurveillees.selected.pattern.color
                                        : MAP_COLORS.zonesSurveillees.pattern.color,
                                    // Line width: thicker for selected state
                                    isSelected 
                                        ? MAP_COLORS.zonesSurveillees.selected.pattern.lineWidth
                                        : MAP_COLORS.zonesSurveillees.pattern.lineWidth,
                                    // Spacing: tighter for selected state
                                    isSelected 
                                        ? MAP_COLORS.zonesSurveillees.selected.pattern.spacing
                                        : MAP_COLORS.zonesSurveillees.pattern.spacing
                                )
                            }),
                            // STROKE: Border styling with state-based appearance
                            stroke: new Stroke({ 
                                color: isSelected 
                                    ? MAP_COLORS.zonesSurveillees.selected.stroke
                                    : MAP_COLORS.zonesSurveillees.stroke, 
                                width: isSelected 
                                    ? MAP_COLORS.zonesSurveillees.selected.strokeWidth
                                    : MAP_COLORS.zonesSurveillees.strokeWidth
                            }),
                            // Z-INDEX: Selected features appear above normal features
                            zIndex: isSelected ? 1 : 0
                        });
                    };

                    const zonesSurveilleesVectorLayer = new VectorLayer({
                        source: zonesSurveilleesSource,
                        className: 'zones-surveillees-layer',
                        style: zonesSurveilleesStyleFunction,
                        zIndex: 10, // Au-dessus de la data-layer (zIndex: 9)
                    });

                    zonesSurveilleesLayer.current = zonesSurveilleesVectorLayer;
                    map.current.addLayer(zonesSurveilleesVectorLayer);

                    // Add hover handler for cursor change
                    const handleZonesSurveilleesHover = (event: any) => {
                        if (isMeasureMode || isGpsPickMode) return; // Don't change cursor in measure or GPS pick mode
                        
                        let featureFound = false;
                        
                        // Use forEachFeatureAtPixel for more reliable feature detection
                        map.current?.forEachFeatureAtPixel(event.pixel, (feature: any, layer: any) => {
                            // Check if the feature belongs to the zones surveillées layer
                            if (layer === zonesSurveilleesVectorLayer) {
                                featureFound = true;
                                return true; // Stop iteration after finding the first feature
                            }
                        }, {
                            layerFilter: (layer: any) => layer === zonesSurveilleesVectorLayer,
                            hitTolerance: 5 // Allow some tolerance for easier hovering
                        });
                        
                        // Update cursor based on feature presence
                        if (map.current) {
                            map.current.getViewport().style.cursor = featureFound ? 'pointer' : '';
                        }
                    };

                    map.current.on('pointermove', handleZonesSurveilleesHover);

                    // Store the handlers for cleanup
                    (zonesSurveilleesVectorLayer as any)._hoverHandler = handleZonesSurveilleesHover;
                } catch (error) {
                    console.error('OLMap - Error processing zones surveillées:', error);
                    console.error('OLMap - Zones surveillées data:', zonesSurveillees);
                }
            }
        }

        return () => {
            if (zonesSurveilleesLayer.current) {
                // Remove handlers before removing layer
                const hoverHandler = (zonesSurveilleesLayer.current as any)._hoverHandler;
                if (map.current && hoverHandler) {
                    map.current.un('pointermove', hoverHandler);
                }
                
                map.current?.removeLayer(zonesSurveilleesLayer.current);
                zonesSurveilleesLayer.current = null;
            }
        };
    }, [zonesSurveillees, isMeasureMode, isGpsPickMode]);

    // Handle arrete perils layer
    useEffect(() => {
        if (!map.current || !arretePerils) return;

        // Add arrete perils data
        if (arretePerils && arretePerils.length > 0) {
            try {
                const arretePerilsSource = new VectorSource({
                    features: new GeoJSON().readFeatures({type: 'FeatureCollection', features: arretePerils}, {
                        featureProjection: 'EPSG:3857'
                    })
                });  

                const arretePerilsVectorLayer = new VectorLayer({
                    source: arretePerilsSource,
                    className: 'arrete-perils-layer',
                    style: new Style({
                        image: new Icon({
                            src: arretePerilsIcon,
                            anchor: [0.5, 0.5], // Anchor at center
                            anchorXUnits: 'fraction',
                            anchorYUnits: 'fraction',
                            // scale: 0.1,
                            width: 30,
                            height: 30,
                            color: MAP_COLORS.arretePerils.fill // Orange color
                        })
                      }),
                    zIndex: 17,
                });

                arretePerilsLayer.current = arretePerilsVectorLayer;
                map.current.addLayer(arretePerilsVectorLayer);

                // Add hover handler for cursor change
                const handleArretePerilHover = (event: any) => {
                    if (isMeasureMode || isGpsPickMode) return; // Don't change cursor in measure or GPS pick mode
                    
                    let featureFound = false;
                    
                    // Use forEachFeatureAtPixel for more reliable feature detection
                    map.current?.forEachFeatureAtPixel(event.pixel, (feature: any, layer: any) => {
                        // Check if the feature belongs to the arrete perils layer
                        if (layer === arretePerilsVectorLayer) {
                            featureFound = true;
                            return true; // Stop iteration after finding the first feature
                        }
                    }, {
                        layerFilter: (layer: any) => layer === arretePerilsVectorLayer,
                        hitTolerance: 5 // Allow some tolerance for easier hovering
                    });
                    
                    // Update cursor based on feature presence
                    if (map.current) {
                        map.current.getViewport().style.cursor = featureFound ? 'pointer' : '';
                    }
                };

                map.current.on('pointermove', handleArretePerilHover);

                // Store the handlers for cleanup
                (arretePerilsVectorLayer as any)._hoverHandler = handleArretePerilHover;
            } catch (error) {
                console.error('OLMap - Error processing arrete perils:', error);
                console.error('OLMap - Arrete perils data:', arretePerils);
            }
        }

        return () => {
            if (arretePerilsLayer.current) {
                // Remove handlers before removing layer
                const hoverHandler = (arretePerilsLayer.current as any)._hoverHandler;
                if (map.current && hoverHandler) {
                    map.current.un('pointermove', hoverHandler);
                }
                
                map.current?.removeLayer(arretePerilsLayer.current);
                arretePerilsLayer.current = null;
            }
        };
    }, [arretePerils, isMeasureMode, isGpsPickMode]);

    // handle risk layer changes
    useEffect(() => {
      if (!map.current) { return }

      if (!layerMap[selectedRisk])  { return }

      const geoServerLayerName = layerMap[selectedRisk]!.name
      const minZoom = layerMap[selectedRisk]?.minZoom || 0
      const maxZoom = layerMap[selectedRisk]?.maxZoom || 18

      let rcpStyle = ''
      if (["wind", "heavyRain"].includes(selectedRisk)) {
          rcpStyle = `data4home:${rcpStyleMap[selectedRcp]!}`
      }

      const layer = createGeoServerRiskTileLayer({
        layers: geoServerLayerName,
        styles: rcpStyle,
        opacity: 0.6,
        zIndex: 9,
        className: "data-layer",
        injectTileBboxViewParams: layerMap[selectedRisk]?.injectTileBboxViewParams ?? false,
      });
      dataLayer.current = layer
      // Add the data layer directly
      map.current.addLayer(layer)
      
      // Note: Vector layers (buildings, parcels, water ways) are added in their respective useEffect
      // and will be automatically included in the map
      map.current.getView().setMaxZoom(maxZoom)
      map.current.getView().setMinZoom(minZoom)

      return () => {
          if (dataLayer.current) {
              map.current?.removeLayer(dataLayer.current)
              dataLayer.current = null
          }
      }

    }, [selectedRisk, selectedRcp])

    // Handle measure mode
    useEffect(() => {
        if (!map.current || !center) return;

        const handleMapClick = (event: any) => {
            if (!isMeasureMode) return;

            const coordinate = event.coordinate;
            const clickedLonLat = transform(coordinate, 'EPSG:3857', 'EPSG:4326');
            const centerLonLat = [center.lng, center.lat];
            
            // Clear existing measure layer
            if (measureLayer.current) {
                map.current?.removeLayer(measureLayer.current);
                measureLayer.current = null;
            }

            // Create marker at clicked point
            const markerFeature = new Feature({
                geometry: new Point(coordinate),
                name: 'Measure Point'
            });
            
            const markerStyle = new Style({
                image: new CircleStyle({
                    radius: 6,
                    fill: new Fill({ color: MAP_COLORS.ui.measure.fill }),
                    stroke: new Stroke({ 
                        color: MAP_COLORS.ui.measure.stroke, 
                        width: MAP_COLORS.ui.measure.strokeWidth 
                    })
                })
            });
            
            markerFeature.setStyle(markerStyle);

            // Create line from center to clicked point
            const lineFeature = new Feature({
                geometry: new LineString([fromLonLat(centerLonLat), coordinate]),
                name: 'Measure Line'
            });
            
            const lineStyle = new Style({
                stroke: new Stroke({
                    color: MAP_COLORS.ui.measureLine.color,
                    width: MAP_COLORS.ui.measureLine.width
                })
            });
            
            lineFeature.setStyle(lineStyle);

            // Add both features to measure layer
            const measureSource = new VectorSource({
                features: [markerFeature, lineFeature]
            });
            
            const measureVectorLayer = new VectorLayer({
                source: measureSource,
                className: 'measure-layer',
                zIndex: 20,
            });
            
            measureLayer.current = measureVectorLayer;
            map.current?.addLayer(measureVectorLayer);
            
            // Calculate distance
            const distance = calculateDistance(centerLonLat, clickedLonLat);
            
            // Update measure info panel
            if (measureInfoControl.current) {
                const infoPanel = measureInfoControl.current.getInfoPanel();
                if (infoPanel) {
                    const distanceEl = infoPanel.querySelector('#measure-distance');
                    const altitudeEl = infoPanel.querySelector('#measure-altitude');
                    
                    if (distanceEl) distanceEl.textContent = `Distance: ${Math.floor(distance)}m`;
                    if (altitudeEl) altitudeEl.textContent = 'Altitude: -';
                    
                    // Get altitude
                    getElevation(clickedLonLat[1], clickedLonLat[0]).then(({ altitude }) => {
                        if (altitudeEl) altitudeEl.textContent = `Altitude: ${Math.floor(altitude)}m`;
                    });
                    
                    infoPanel.style.display = 'block';
                }
            }
        };

        if (isMeasureMode) {
            map.current.on('click', handleMapClick);
            map.current.getViewport().style.cursor = 'crosshair';
            
            // Update button appearance
            if (measureControl.current) {
                measureControl.current.setActive(true);
            }
        } else {
            map.current.un('click', handleMapClick);
            map.current.getViewport().style.cursor = '';
            
            // Update button appearance
            if (measureControl.current) {
                measureControl.current.setActive(false);
            }
            
            // Clear measure layer
            if (measureLayer.current) {
                map.current.removeLayer(measureLayer.current);
                measureLayer.current = null;
            }
            
            // Hide measure info panel
            if (measureInfoControl.current) {
                const infoPanel = measureInfoControl.current.getInfoPanel();
                if (infoPanel) {
                    infoPanel.style.display = 'none';
                }
            }
        }

        return () => {
            if (map.current) {
                map.current.un('click', handleMapClick);
            }
        };
    }, [isMeasureMode, center]);

    // Handle measure button click - ensure it toggles correctly; turn off GPS pick when measure is activated
    useEffect(() => {
        if (!measureControl.current) return;

        const handleClick = () => {
            setIsMeasureMode(prev => {
                if (!prev) setIsGpsPickMode(false);
                return !prev;
            });
        };

        measureControl.current.setOnClick(handleClick);
    }, [isMeasureMode]);

    // Handle GPS pick button click - turn off measure when GPS pick is activated
    useEffect(() => {
        if (!gpsPickControl.current || !onCoordinatesSelected) return;

        const handleClick = () => {
            setIsGpsPickMode(prev => {
                if (!prev) setIsMeasureMode(false);
                return !prev;
            });
        };

        gpsPickControl.current.setOnClick(handleClick);
    }, [isGpsPickMode, onCoordinatesSelected]);

    // Handle GPS pick mode: map click returns coordinates then turns off mode
    useEffect(() => {
        if (!map.current || !onCoordinatesSelected) return;

        const handleGpsPickClick = (event: any) => {
            if (!isGpsPickMode) return;
            const coordinate = event.coordinate;
            const [lng4326, lat4326] = transform(coordinate, 'EPSG:3857', 'EPSG:4326');
            onCoordinatesSelected(lat4326, lng4326);
            setIsGpsPickMode(false);
        };

        if (isGpsPickMode) {
            map.current.on('click', handleGpsPickClick);
            map.current.getViewport().style.cursor = 'crosshair';
            gpsPickControl.current?.setActive(true);
        } else {
            map.current.un('click', handleGpsPickClick);
            map.current.getViewport().style.cursor = '';
            gpsPickControl.current?.setActive(false);
        }

        return () => {
            if (map.current) map.current.un('click', handleGpsPickClick);
        };
    }, [isGpsPickMode, onCoordinatesSelected]);

    /**
     * MAP CLICK INTERACTION HANDLER
     * =============================
     * 
     * This useEffect manages all click interactions on the map, including:
     * - Heavy Rain feature clicks
     * - Historic Monument feature clicks  
     * - Zones Surveillées feature clicks
     * - Panel management and positioning
     * 
     * INTERACTION FLOW:
     * 1. Check if measure mode is active (skip interactions if true)
     * 2. Detect which layer was clicked using forEachFeatureAtPixel
     * 3. Handle layer-specific interactions (styling, panel opening)
     * 4. Manage panel positioning to avoid covering clicked features
     * 5. Update visual feedback and state management
     */
    useEffect(() => {
        if (!map.current) return;

        const handleMapClick = (event: any) => {
            // Skip interactions during measure or GPS pick mode
            if (isMeasureMode || isGpsPickMode) return;

            let clickedOnFeature = false;
            
            /**
             * FEATURE DETECTION AND INTERACTION HANDLING
             * ==========================================
             * 
             * The forEachFeatureAtPixel method iterates through all features at the click point.
             * We use layer filtering to only check specific interactive layers.
             * 
             * LAYER PRIORITY (checked in order):
             * 1. Heavy Rain Layer - Red circles for weather observations
             * 2. Historic Monuments Layer - Purple circles for monuments
             * 3. Zones Surveillées Layer - Pattern-filled polygons for zones
             */
            map.current?.forEachFeatureAtPixel(event.pixel, (feature: any, layer: any) => {

                // Clear heavy rain selections
                selectedHeavyRainFeature.current?.setStyle(undefined);
                selectedHeavyRainFeature.current = null;

                // Clear zones surveillées selections
                selectedZoneSurveilleeFeatures.current?.forEach(f => f.set('isSelected', false));
                selectedZoneSurveilleeFeatures.current = [];
                
                // Clear historic monuments selections
                selectedHistoricMonumentFeatures.current?.forEach(f => f.setStyle(undefined));
                selectedHistoricMonumentFeatures.current = [];

                // Clear zones surveillées selections
                selectedZoneSurveilleeFeatures.current?.forEach(f => f.set('isSelected', false));
                selectedZoneSurveilleeFeatures.current = [];

                // Clear arrete perils selections
                selectedArretePerilFeatures.current?.forEach(f => f.setStyle(undefined));
                selectedArretePerilFeatures.current = [];
                
                // ================================================================
                // HEAVY RAIN FEATURE INTERACTION
                // ================================================================
                if (layer === heavyRainLayer.current) {
                    clickedOnFeature = true;
                    
                    // PANEL POSITIONING: Check if feature will be hidden under panel
                    if (map.current && isFeatureUnderPanel(event.coordinate, map.current)) {
                        panToAvoidPanel(map.current, event.coordinate);
                    }
                    
                    // VISUAL FEEDBACK: Clear previous highlights and update selection style
                    // Clear previous heavy rain selection
                    if (selectedHeavyRainFeature.current && selectedHeavyRainFeature.current !== feature) {
                        selectedHeavyRainFeature.current.setStyle(undefined);
                    }

                    feature.setStyle(new Style({
                        image: new CircleStyle({
                            radius: MAP_COLORS.heavyRain.radius,
                            fill: new Fill({ color: MAP_COLORS.heavyRain.selected.fill }),
                            stroke: new Stroke({ 
                                color: MAP_COLORS.heavyRain.selected.stroke, 
                                width: MAP_COLORS.heavyRain.selected.strokeWidth 
                            })
                        })
                    }));
                    selectedHeavyRainFeature.current = feature;
                    
                    // PANEL MANAGEMENT: Open side panel with feature data
                    const properties = feature.getProperties();
                    const featureData = {
                        ...properties,
                        date: moment(properties.time_event).format('DD/MM/YYYY')
                    };
                    setHeavyRainFeature(featureData);
                    setPanelContentType('heavyRain');
                    setPanelOpen(true);
                    return true;
                }
                
                // ================================================================
                // HISTORIC MONUMENT FEATURE INTERACTION
                // ================================================================
                if (layer === historicMonumentsLayer.current) {
                    clickedOnFeature = true;
                    
                    // PANEL POSITIONING: Check if feature will be hidden under panel
                    if (map.current && isFeatureUnderPanel(event.coordinate, map.current)) {
                        panToAvoidPanel(map.current, event.coordinate);
                    }
                    
                    // VISUAL FEEDBACK: Clear previous highlights and apply new selection
                    // Clear heavy rain selections
                    if (selectedHeavyRainFeature.current) {
                        selectedHeavyRainFeature.current.setStyle(undefined);
                        selectedHeavyRainFeature.current = null;
                    }
                    
                    // Apply highlight style to clicked monument
                    feature.setStyle(new Style({
                        image: new Icon({
                            src: monumentIcon,
                            anchor: [0.5, 0.5], // Anchor at center
                            anchorXUnits: 'fraction',
                            anchorYUnits: 'fraction',
                            scale: 1.1, // Slightly larger for selected state
                            color: MAP_COLORS.historicMonuments.selected.color // Orange color
                        })
                    }));
                    selectedHistoricMonumentFeatures.current.push(feature);
                    
                    // PANEL MANAGEMENT: Prepare monument data and open panel
                    const properties = feature.getProperties();
                    const monumentData = [{
                        properties: properties,
                        geometry: feature.getGeometry()
                    }];
                    
                    setHistoricMonumentFeatures(monumentData);
                    setPanelContentType('historicMonuments');
                    setPanelOpen(true);
                    return true;
                }
                
                // ================================================================
                // ZONES SURVEILLÉES FEATURE INTERACTION
                // ================================================================
                if (layer === zonesSurveilleesLayer.current) {
                    clickedOnFeature = true;
                    
                    // PANEL POSITIONING: Check if feature will be hidden under panel
                    if (map.current && isFeatureUnderPanel(event.coordinate, map.current)) {
                        panToAvoidPanel(map.current, event.coordinate);
                    }
                    
                    // MULTI-ZONE DETECTION: Find ALL zones that intersect with the click point
                    // This is important because zones can overlap, and we want to show all relevant zones
                    const allIntersectingZones: any[] = [];
                    const source = zonesSurveilleesLayer.current.getSource();
                    
                    if (source) {
                        source.forEachFeature((zoneFeature: any) => {
                            const geometry = zoneFeature.getGeometry();
                            if (geometry && geometry.intersectsCoordinate(event.coordinate)) {
                                // FEATURE STATE: Mark as selected (triggers style function update)
                                zoneFeature.set('isSelected', true);
                                selectedZoneSurveilleeFeatures.current.push(zoneFeature);
                                
                                // DATA COLLECTION: Prepare zone data for panel display
                                const properties = zoneFeature.getProperties();
                                allIntersectingZones.push({
                                    properties: properties,
                                    geometry: zoneFeature.getGeometry()
                                });
                            }
                        });
                    }
                    
                    // PANEL MANAGEMENT: Open panel with all intersecting zones
                    setZoneSurveilleeFeatures(allIntersectingZones);
                    setPanelContentType('zonesSurveillees');
                    setPanelOpen(true);
                    return true;
                }

                // ================================================================
                // FIRE STATION FEATURE INTERACTION
                // ================================================================
                if (layer === fireStationsLayer.current || layer === fireStationRouteLayer.current) {
                    clickedOnFeature = true;
                    centerOnFireStationRef.current();
                    return true;
                }

                if (layer === gasStationsLayer.current) {
                    clickedOnFeature = true;
                    centerOnGasStationRef.current();
                    return true;
                }

                // ================================================================
                // ARRETE PERILS FEATURE INTERACTION
                // ================================================================
                if (layer === arretePerilsLayer.current) {
                    clickedOnFeature = true;
                    
                    // PANEL POSITIONING: Check if feature will be hidden under panel
                    if (map.current && isFeatureUnderPanel(event.coordinate, map.current)) {
                        panToAvoidPanel(map.current, event.coordinate);
                    }
                    
                    // Apply highlight style to clicked monument
                    feature.setStyle(new Style({
                        image: new Icon({
                            src: arretePerilsIcon,
                            anchor: [0.5, 0.5], // Anchor at center
                            anchorXUnits: 'fraction',
                            anchorYUnits: 'fraction',
                            // scale: 0.1,
                            width: 30,
                            height: 30,
                            color: MAP_COLORS.arretePerils.selected.fill // Orange color
                        })
                    }));
                    selectedArretePerilFeatures.current.push(feature);
                    
                    // PANEL MANAGEMENT: Prepare monument data and open panel
                    const properties = feature.getProperties();
                    const arretePerilData = [{
                        properties: properties,
                        geometry: feature.getGeometry()
                    }];
                    
                    setArretePerilsFeatures(arretePerilData);
                    setPanelContentType('arretePerils');
                    setPanelOpen(true);
                    return true;
                }

            }, {
                // LAYER FILTERING: Only check interactive layers for performance
                layerFilter: (layer: any) => layer === heavyRainLayer.current || layer === historicMonumentsLayer.current || layer === zonesSurveilleesLayer.current || layer === arretePerilsLayer.current || layer === fireStationsLayer.current || layer === fireStationRouteLayer.current || layer === gasStationsLayer.current,
                hitTolerance: 5 // Allow some tolerance for easier clicking
            });

            /**
             * PANEL CLEANUP: Handle clicks on empty areas
             * ===========================================
             * 
             * When clicking on empty areas (not on features), we need to:
             * 1. Close any open panels
             * 2. Reset all visual highlights
             * 3. Clear selection states
             * 4. Reset panel content
             */
            if (
                !clickedOnFeature &&
                (panelOpen || selectedZoneSurveilleeFeatures.current.length > 0)
            ) {
                // Reset heavy rain feature style
                if (selectedHeavyRainFeature.current) {
                    selectedHeavyRainFeature.current.setStyle(undefined);
                    selectedHeavyRainFeature.current = null;
                }
                
                // Reset historic monument feature styles
                selectedHistoricMonumentFeatures.current.forEach(feature => {
                    feature.setStyle(undefined);
                });
                selectedHistoricMonumentFeatures.current = [];
                
                // Reset zones surveillées (layer style = f(isSelected))
                selectedZoneSurveilleeFeatures.current.forEach(feature => {
                    feature.set('isSelected', false);
                });
                selectedZoneSurveilleeFeatures.current = [];
                selectedZonesSurveillees.current = [];

                selectedArretePerilFeatures.current.forEach(feature => {
                    feature.setStyle(undefined);
                });
                selectedArretePerilFeatures.current = [];
                
                setHeavyRainFeature(null);
                setHistoricMonumentFeatures([]);
                setZoneSurveilleeFeatures([]);
                setArretePerilsFeatures([]);
                setPanelOpen(false);
                setPanelContentType(null);
            }
        };

        map.current.on('click', handleMapClick);

        return () => {
            if (map.current) {
                map.current.un('click', handleMapClick);
            }
        };
    }, [panelOpen, isMeasureMode, isGpsPickMode, heavyRainLayer, historicMonumentsLayer, zonesSurveilleesLayer, arretePerilsLayer]);

    // Reset selected feature style and panel data when side panel closes
    useEffect(() => {
        if (!panelOpen) {
            setZoneSurveilleeFeatures([]);
            setHistoricMonumentFeatures([]);
            setHeavyRainFeature(null);
            setArretePerilsFeatures([]);

            if (selectedHeavyRainFeature.current) {
                selectedHeavyRainFeature.current.setStyle(undefined);
                selectedHeavyRainFeature.current = null;
            }

            if (selectedHistoricMonumentFeatures.current.length > 0) {
                selectedHistoricMonumentFeatures.current.forEach(feature => {
                    feature.setStyle(undefined);
                });
                selectedHistoricMonumentFeatures.current = [];
            }

            if (selectedZoneSurveilleeFeatures.current.length > 0) {
                selectedZoneSurveilleeFeatures.current.forEach(feature => {
                    feature.set('isSelected', false);
                });
                selectedZoneSurveilleeFeatures.current = [];
            }
            selectedZonesSurveillees.current = [];

            if (selectedArretePerilFeatures.current.length > 0) {
                selectedArretePerilFeatures.current.forEach(feature => {
                    feature.setStyle(undefined);
                });
                selectedArretePerilFeatures.current = [];
            }
        }
    }, [panelOpen, selectedArretePerilFeatures, selectedHeavyRainFeature, selectedHistoricMonumentFeatures, selectedZoneSurveilleeFeatures]);

    // Handle external selection of historic monuments
    useEffect(() => {
        if (historicMonumentFeatures && historicMonumentFeatures.length > 0 && panelContentType === 'historicMonuments') {
            highlightSelectedHistoricMonuments(historicMonumentFeatures);
        }
    }, [historicMonumentFeatures, panelContentType, highlightSelectedHistoricMonuments]);

    // Handle external selection of zones surveillées
    useEffect(() => {
        if (zoneSurveilleeFeatures && zoneSurveilleeFeatures.length > 0 && panelContentType === 'zonesSurveillees') {
            highlightSelectedZonesSurveillees(zoneSurveilleeFeatures);
        }
    }, [zoneSurveilleeFeatures, panelContentType, highlightSelectedZonesSurveillees]);

    return (
        <div style={{position: "relative", ...style}}>
            <div style={{...style}} ref={mapElement} className="map-container">
            </div>
            <SidePanel 
                isOpen={panelOpen}
                contentType={panelContentType}
                onClose={() => {
                    setPanelOpen(false);
                    setPanelContentType(null);
                    setZoneSurveilleeFeatures([]);
                    setHistoricMonumentFeatures([]);
                    setHeavyRainFeature(null);
                    setArretePerilsFeatures([]);
                }}
                heavyRainFeature={heavyRainFeature}
                historicMonuments={historicMonumentFeatures}
                zonesSurveillees={zoneSurveilleeFeatures}
                arretePerils={arretePerilsFeatures}
                fireStation={fireStations?.features?.[0]}
                gasStation={gasStations?.features?.[0]}
            />
            { selectedRisk && layerMap[selectedRisk] && (!!layerMap[selectedRisk]?.minZoom || !!layerMap[selectedRisk]?.maxZoom) &&
                <div style={{position: "absolute", left: "0", bottom: "0", padding: "0 10px 0 10px", display: "flex", flexDirection: "column", alignContent: "center", justifyContent: "center", backgroundColor: MAP_COLORS.ui.overlay.dark}}>
                    <span style={{flex: 0, fontSize: "1em", color: "white", textAlign: "center", fontWeight: "bold"}}>{t("map.limitedZoomLevelMessage")}</span>
                </div>
            }
            { selectedRisk && layerMap[selectedRisk] == null &&
                <div style={{position: "absolute", top: "0", left: "0", bottom: "0", right: "0", display: "flex", flexDirection: "column", alignContent: "center", justifyContent: "center", backgroundColor: MAP_COLORS.ui.overlay.darker}}>
                    <span style={{flex: 0, fontSize: "2em", color: MAP_COLORS.ui.overlay.error, textAlign: "center", fontWeight: "bold"}}>{t("map.underConstruction")}</span>
                </div>
            }
        </div>
    );
});

OLMap.displayName = 'OLMap';

export default OLMap;